import React, { useEffect, useState, useRef, useMemo, lazy, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import "./App.css";
// Code-splitting: AdminPage and AffiliatePage are ~5000 lines combined.
// • `lazy()` keeps them OUT of the main homepage bundle (homepage stays fast).
// • `webpackPrefetch: true` adds <link rel="prefetch"> so the browser downloads
//   each chunk in idle time AFTER the homepage finishes loading — so when an
//   admin/affiliate user navigates to /admin or /affiliate, the chunk is
//   already cached and the page appears instantly.
const AffiliatePage = lazy(() => import(/* webpackChunkName: "affiliate", webpackPrefetch: true */ "./AffiliatePage"));
const AdminPage = lazy(() => import(/* webpackChunkName: "admin", webpackPrefetch: true */ "./AdminPage"));
import { TermsPage, PrivacyPage } from "./Legal";
import { AuthProvider, AuthCallback, SignInModal, useAuth } from "./auth";
import { ImageUploader, uploadImage } from "./ImageUploader";
import { I18nProvider, useI18n, LanguageSwitcher, useCatT, tCatTitle, tCatTag } from "./i18n";
import { cdnUrl } from "./cdn";

// uploadImage retained as named import (used by some legacy spots)
void uploadImage;

// Cookies travel with cross-site axios calls (httpOnly session)
axios.defaults.withCredentials = false;

// Global retry interceptor: silently re-attempts GET requests that fail with a
// transient gateway error (502/503/504/520-524 from Cloudflare/Emergent edge).
// Up to 2 retries with exponential backoff. Prevents "Error loading X" banners
// from showing up for one-off network blips the user would never see otherwise.
axios.interceptors.response.use(undefined, async (error) => {
  const cfg = error?.config || {};
  const status = error?.response?.status;
  const isGet = (cfg.method || "get").toLowerCase() === "get";
  const transient = !status || status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 524);
  if (!isGet || !transient) return Promise.reject(error);
  cfg.__zcRetry = (cfg.__zcRetry || 0) + 1;
  if (cfg.__zcRetry > 2) return Promise.reject(error);
  const delay = 400 * Math.pow(2, cfg.__zcRetry - 1); // 400ms, 800ms
  await new Promise(r => setTimeout(r, delay));
  return axios(cfg);
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADULT_CATEGORIES = new Set(["stripclubs", "adultent", "dispensaries"]);

// Sub-category picker (owner Portal): text input backed by a <datalist> that pulls
// suggestions from /api/categories/subcategories for the currently-selected category.
function SubcategoryPicker({ category, value, onChange, testId, placeholder = "Sub-category" }) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (!category) { setOpts([]); return; }
    let cancelled = false;
    axios.get(`${API}/categories/subcategories?category=${encodeURIComponent(category)}`)
      .then(r => { if (!cancelled) setOpts(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setOpts([]); });
    return () => { cancelled = true; };
  }, [category]);
  const listId = `sub-opts-${testId || Math.random().toString(36).slice(2, 8)}`;
  return (
    <>
      <input
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        list={listId}
        placeholder={placeholder}
        className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm"
      />
      <datalist id={listId}>
        {opts.map(o => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

// Default category list (fallback if /api/categories/full hasn't loaded yet).
// Live values are merged in at boot — admins can change image, title, tag,
// reorder, hide, or add custom categories via Admin → Categories.
const DEFAULT_CATEGORIES = [
  // ── TOP 14 (user-specified order) ──
  { key: "whatsnew", title: "What's New", tag: "Arrivals", img: "https://images.unsplash.com/photo-1670132898415-926edddb82d8?w=600" },
  { key: "happening", title: "Happening This Week", tag: "Mon-Sun · resets weekly", img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600", isHappening: true },
  { key: "happyhours", title: "Happy Hours", tag: "Locals Only", img: "https://images.unsplash.com/photo-1765130803583-a5de64bbcc70?w=600" },
  { key: "bars", title: "Bars & Lounges", tag: "Mixology", img: "https://images.pexels.com/photos/5371926/pexels-photo-5371926.jpeg?w=600" },
  { key: "restaurants", title: "Restaurants", tag: "Gastronomy", img: "https://images.pexels.com/photos/24186393/pexels-photo-24186393.jpeg?w=600" },
  { key: "hotdeals", title: "Hot Deals", tag: "Tonight Only", img: "https://images.unsplash.com/photo-1535132011086-b8818f016104?w=600" },
  { key: "dispensaries", title: "Dispensaries", tag: "21+ Cannabis", img: "https://images.unsplash.com/photo-1603909223429-69bb7101f420?w=600" },
  { key: "tattoos", title: "Tattoo Shops", tag: "Body Art", img: "https://images.unsplash.com/photo-1565058379802-bbe93b2f703a?w=600" },
  { key: "stripclubs", title: "Gentlemen's Clubs", tag: "21+", img: "https://images.unsplash.com/photo-1667396219511-e217e8958952?w=600" },
  { key: "clubs", title: "Nightclubs", tag: "After Dark", img: "https://images.pexels.com/photos/1494666/pexels-photo-1494666.jpeg?w=600" },
  { key: "wedding", title: "Wedding", tag: "Say I Do", img: "https://images.unsplash.com/photo-1771775751167-ae3b5ab935fd?w=600" },
  { key: "seenontv", title: "Seen on TV", tag: "Hollywood Vegas", img: "https://images.unsplash.com/photo-1509248961158-e54f6934749c?w=600" },
  { key: "smokeshop", title: "Smoke Shops", tag: "Glass & Vape", img: "https://images.unsplash.com/photo-1603909223429-69bb7101f420?w=600" },
  { key: "liquor", title: "Liquor Stores", tag: "Allocated Pours", img: "https://images.pexels.com/photos/11035323/pexels-photo-11035323.jpeg?w=600" },
  { key: "fremontstreet", title: "Fremont Street", tag: "Old Vegas", img: "https://images.unsplash.com/photo-1581351721010-8cf859cb14a4?w=600" },
  { key: "freetodo", title: "Free to Do", tag: "$0", img: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=600" },
  // ── Remaining categories ──
  { key: "events", title: "Events", tag: "Calendar", img: "https://images.unsplash.com/photo-1504707748692-419802cf939d?w=600" },
  { key: "shows", title: "Entertainment & Tourism", tag: "Residencies", img: "https://images.pexels.com/photos/6782458/pexels-photo-6782458.jpeg?w=600" },
  { key: "poolparties", title: "Pool Parties", tag: "Dayclubs", img: "https://images.pexels.com/photos/11185925/pexels-photo-11185925.jpeg?w=600" },
  { key: "dayclubs", title: "Day Clubs", tag: "Sun's Out", img: "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=600" },
  { key: "hookah", title: "Hookah Lounges", tag: "After Dinner", img: "https://images.pexels.com/photos/3932945/pexels-photo-3932945.jpeg?w=600" },
  { key: "cigar", title: "Cigar Lounges", tag: "Slow Burn", img: "https://images.unsplash.com/photo-1574096079513-d8259312b785?w=600" },
  { key: "casinos", title: "Casinos", tag: "High Limit", img: "https://images.pexels.com/photos/7594184/pexels-photo-7594184.jpeg?w=600" },
  { key: "casinogaming", title: "Casino & Gaming", tag: "Action", img: "https://images.unsplash.com/photo-1606167668584-78701c57f13d?w=600" },
  { key: "hoteldeals", title: "Hotel Deals", tag: "Stay", img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600" },
  { key: "airlinedeals", title: "Airline Deals", tag: "Fly", img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600" },
  { key: "tours", title: "Tours", tag: "Guided", img: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600" },
  { key: "helicopter", title: "Helicopter Rides", tag: "Take Flight", img: "https://images.pexels.com/photos/2425011/pexels-photo-2425011.jpeg?w=600" },
  { key: "shootingrange", title: "Shooting Range", tag: "Lock & Load", img: "https://images.pexels.com/photos/1545743/pexels-photo-1545743.jpeg?w=600" },
  { key: "kidfriendly", title: "Kid Friendly", tag: "For the Family", img: "https://images.unsplash.com/photo-1560419015-7c427e8ae5ba?w=600" },
  { key: "outdoor", title: "Outdoor & Adventure", tag: "Desert & Sky", img: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=600" },
  { key: "shopping", title: "Shopping", tag: "Retail Therapy", img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600" },
  { key: "souvenir", title: "Souvenir Shops", tag: "Take it Home", img: "https://images.unsplash.com/photo-1555529902-5261145633bf?w=600" },
  { key: "transportation", title: "Transportation", tag: "Get Around", img: "https://images.unsplash.com/photo-1590362891991-f776e747a588?w=600" },
  { key: "conventions", title: "Conventions", tag: "Business", img: "https://images.unsplash.com/photo-1515169067868-5387ec356754?w=600" },
  { key: "eventspaces", title: "Event Spaces", tag: "Host It", img: "https://images.pexels.com/photos/265947/pexels-photo-265947.jpeg?w=600" },
  { key: "banquethalls", title: "Banquet Halls", tag: "Celebrate", img: "https://images.unsplash.com/photo-1519741497674-611481863552?w=600" },
  { key: "attractions", title: "Attractions", tag: "Thrills", img: "https://images.unsplash.com/photo-1671573576055-b6ac57f8ec51?w=600" },
  { key: "adultent", title: "Adult Entertainment", tag: "21+", img: "https://images.unsplash.com/photo-1559519529-0936e4058364?w=600" },
  { key: "spas", title: "Spas & Wellness", tag: "Restore", img: "https://images.pexels.com/photos/36177119/pexels-photo-36177119.jpeg?w=600" },
  { key: "attorneys", title: "Attorneys", tag: "Legal Help", img: "https://images.unsplash.com/photo-1589391886645-d51941baf7fb?w=600" },
  { key: "karaoke", title: "Karaoke", tag: "Sing the Strip", img: "https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=600" },
];

// Live mutable array — components read this at render time. We replace its
// contents in-place so non-React readers (filters, lookups) always see the
// latest data, then notify subscribers to trigger re-renders.
// Perf: seed from localStorage cache on cold start so repeat visits skip the
// "old image flash → new image" flicker entirely (1 paint instead of 2).
// Bump the cache version (v2) whenever the default image set or category schema
// changes — clients with a v1 cache will refetch on next visit.
const __readCachedCategories = () => {
  try {
    const raw = localStorage.getItem("zc_categories_cache_v2");
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data) && data.length ? data : null;
  } catch { return null; }
};
const CATEGORIES = __readCachedCategories() || [...DEFAULT_CATEGORIES];
let CAT_TITLE = Object.fromEntries(CATEGORIES.map(c => [c.key, c.title]));

const __categorySubs = new Set();
function __setLiveCategories(next) {
  if (!Array.isArray(next) || !next.length) return;
  // Merge: ensure we never lose entries that the API hasn't returned (defensive).
  CATEGORIES.length = 0;
  next.forEach(c => CATEGORIES.push(c));
  CAT_TITLE = Object.fromEntries(CATEGORIES.map(c => [c.key, c.title]));
  // Persist for next cold start — eliminates the old-image flash on repeat visits.
  try { localStorage.setItem("zc_categories_cache_v2", JSON.stringify(next)); } catch { /* quota / disabled */ }
  __categorySubs.forEach(fn => { try { fn(); } catch (e) { /* noop */ } });
}
function useCategoriesLive() {
  const [, force] = useState(0);
  useEffect(() => {
    const sub = () => force(v => v + 1);
    __categorySubs.add(sub);
    return () => __categorySubs.delete(sub);
  }, []);
}

// Color schemes for banner ads (by slot) — each MUST visually pop vs gold/black category bars
const SLOT_THEMES = {
  new_deals:    { from: "#ff2d92", to: "#ff7a00", glow: "rgba(255,45,146,0.55)" },
  after_hours:  { from: "#22e3ff", to: "#9b5cff", glow: "rgba(34,227,255,0.55)" },
  weekender:    { from: "#ffb400", to: "#ff5e62", glow: "rgba(255,180,0,0.55)" },
  locals_only:  { from: "#a8ff35", to: "#00d68f", glow: "rgba(168,255,53,0.55)" },
  vip_only:     { from: "#b14bff", to: "#5a1aff", glow: "rgba(177,75,255,0.55)" },
  hidden_gems:  { from: "#ff5083", to: "#ff9d6b", glow: "rgba(255,80,131,0.55)" },
  happy_hour:   { from: "#ff8c42", to: "#ffcf40", glow: "rgba(255,140,66,0.55)" },
  poolside:     { from: "#00d4ff", to: "#00c07a", glow: "rgba(0,212,255,0.55)" },
};

const getUserId = () => {
  // Legacy — kept for migration only. Returns a UUID stored in localStorage that is migrated to authenticated user_id on first sign-in.
  let id = localStorage.getItem("zc_user_id");
  if (!id) { id = "u_" + Math.random().toString(36).slice(2, 11); localStorage.setItem("zc_user_id", id); }
  return id;
};

// Module-level set + localStorage-backed per-day dedupe — stores ad-impression
// keys already counted on THIS device today. Prevents carousel auto-rotation
// + page refresh + repeat-modal-open from inflating analytics. Previous
// in-memory-only dedupe meant impressions reset on every page refresh, which
// produced ad-impression counts 10-20× the actual visitor count. With this,
// each unique ad slot fires at most once per browser per UTC day.
const __firedAdKeys = (() => {
  try {
    const today = new Date().toISOString().slice(0, 10); // UTC YYYY-MM-DD
    const raw = localStorage.getItem("zc_ad_fired_v1");
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && parsed.day === today && Array.isArray(parsed.keys)) {
      const set = new Set(parsed.keys);
      // Wrap add() so every insertion is persisted (no batching needed for
      // a Set this small — at most a few dozen ad keys/day).
      const origAdd = set.add.bind(set);
      set.add = (k) => {
        origAdd(k);
        try {
          localStorage.setItem("zc_ad_fired_v1", JSON.stringify({ day: today, keys: Array.from(set) }));
        } catch { /* quota or private-mode — fall back to in-memory dedupe */ }
        return set;
      };
      return set;
    }
    // Stale or missing — start a fresh day.
    const set = new Set();
    const origAdd = set.add.bind(set);
    set.add = (k) => {
      origAdd(k);
      try {
        localStorage.setItem("zc_ad_fired_v1", JSON.stringify({ day: today, keys: Array.from(set) }));
      } catch { /* same as above */ }
      return set;
    };
    return set;
  } catch {
    return new Set(); // bulletproof fallback
  }
})();

function TopNav({ onBusinessClick, onCasinoTeamClick, onHome, query, setQuery, activeFilter, setActiveFilter, user, onLogin, onLogout }) {
  useCategoriesLive();
  const { t, lang } = useI18n();
  const [bizDropOpen, setBizDropOpen] = useState(false);
  const bizDropRef = React.useRef(null);
  useEffect(() => {
    const onClickAway = (e) => {
      if (bizDropRef.current && !bizDropRef.current.contains(e.target)) setBizDropOpen(false);
    };
    if (bizDropOpen) document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [bizDropOpen]);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-2xl bg-bg/75 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-4">
          <button data-testid="logo-home-btn" onClick={onHome} className="flex items-center gap-2 group shrink-0">
            <img
              src="/brand/zipcity-logo-v5.png"
              alt="ZipCityLV — Las Vegas Category Search Directory"
              className="h-12 sm:h-28 lg:h-32 w-auto transition-all duration-500 group-hover:scale-[1.04] zc-logo-glow"
              style={{ objectFit: "contain" }}
            />
          </button>
          <div className="flex items-center gap-1 sm:gap-2">
            <LanguageSwitcher />
            <div className="relative" ref={bizDropRef}>
              <button
                data-testid="business-portal-btn"
                onClick={() => setBizDropOpen((v) => !v)}
                className="font-display uppercase text-[10px] sm:text-[11px] tracking-[0.18em] sm:tracking-[0.22em] whitespace-nowrap px-3 sm:px-4 py-2 sm:py-2.5 border border-gold/40 text-gold hover:bg-gold hover:text-bg transition-all duration-300 rounded-sm flex items-center gap-1"
                aria-expanded={bizDropOpen}
              >
                {t("nav.businesses")}
                <span className={`text-[10px] transition-transform ${bizDropOpen ? "rotate-180" : ""}`}>▾</span>
              </button>
              {bizDropOpen && (
                <div data-testid="business-portal-dropdown" className="absolute right-0 mt-2 w-64 bg-surface border border-gold/30 rounded-sm shadow-2xl shadow-black/60 overflow-hidden z-50">
                  <button
                    data-testid="biz-drop-owner"
                    onClick={() => { setBizDropOpen(false); onBusinessClick && onBusinessClick(); }}
                    className="w-full text-left px-4 py-3 hover:bg-gold/10 transition-colors border-b border-white/5"
                  >
                    <div className="font-display uppercase text-[11px] tracking-[0.2em] text-gold">Business</div>
                    <div className="text-muted text-[11px] mt-0.5 font-serif-accent">Own a bar / club / restaurant?</div>
                  </button>
                  <button
                    data-testid="biz-drop-casino"
                    onClick={() => { setBizDropOpen(false); onCasinoTeamClick && onCasinoTeamClick(); }}
                    className="w-full text-left px-4 py-3 hover:bg-purple-500/10 transition-colors"
                  >
                    <div className="font-display uppercase text-[11px] tracking-[0.2em] bg-gradient-to-r from-purple-400 to-gold bg-clip-text text-transparent">Casino Business</div>
                    <div className="text-muted text-[11px] mt-0.5 font-serif-accent">Enterprise / team-member sign-in</div>
                  </button>
                </div>
              )}
            </div>
            {user ? (
              <div className="flex items-center gap-2">
                {user.picture && <img alt="" src={user.picture} className="w-8 h-8 rounded-full border border-gold/30" />}
                <button data-testid="logout-btn" onClick={onLogout} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 text-muted hover:text-white">{t("nav.signout")}</button>
              </div>
            ) : (
              <button data-testid="signin-btn" onClick={onLogin} className="font-display uppercase text-[10px] tracking-[0.2em] whitespace-nowrap px-2 sm:px-3 py-2 text-muted hover:text-gold">{t("nav.signin")}</button>
            )}
          </div>
        </div>
        <div className="pb-4 space-y-3">
          <div className="relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            <input data-testid="search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={t("nav.search")}
              className="w-full bg-surface/60 border border-white/5 pl-11 pr-4 py-3.5 rounded-sm text-sm font-sans focus:border-gold/40 focus:bg-surface transition-all placeholder:text-muted/70" />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {["all", ...CATEGORIES.map((c) => c.key)].map((key) => {
              const c = CATEGORIES.find(x => x.key === key);
              const label = key === "all" ? t("filter.all") : (c ? tCatTitle(c, lang) : key);
              const active = activeFilter === key;
              return (
                <button key={key} data-testid={`filter-chip-${key}`} onClick={() => {
                  setActiveFilter(key);
                  if (key !== "all") {
                    axios.post(`${API}/analytics/track`, { event: "category_visit", key }).catch(() => {});
                  }
                }}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-display uppercase tracking-[0.15em] transition-all duration-300 ${active ? "bg-gold text-bg border border-gold shadow-gold" : "bg-transparent text-muted border border-white/10 hover:border-gold/40 hover:text-gold"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { t } = useI18n();
  return (
    <section className="relative overflow-hidden border-b border-white/5">
      <div className="absolute inset-0 bg-cover bg-center opacity-70" style={{ backgroundImage: "url('/brand/hero-vegas-night.jpeg')" }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at top left, rgba(236,72,153,0.28), transparent 55%), radial-gradient(ellipse at top right, rgba(6,182,212,0.28), transparent 55%), radial-gradient(ellipse at bottom, rgba(212,175,55,0.22), transparent 60%)" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-bg/55 via-bg/35 to-bg" />

      <button
        onClick={() => window.scrollBy({ top: window.innerHeight * 0.55, behavior: "smooth" })}
        data-testid="hero-swipe-down"
        aria-label="Scroll down"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 flex flex-col items-center gap-1 text-gold/80 hover:text-gold transition-colors"
      >
        <span className="font-display uppercase text-[9px] sm:text-[10px] tracking-[0.22em]">{t("hero.swipe")}</span>
        <span className="inline-block animate-bounce text-2xl sm:text-3xl leading-none">↓</span>
      </button>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-7 sm:py-14 lg:py-20">
        <div className="font-serif-accent text-gold text-xs sm:text-base mb-2 sm:mb-4">{t("hero.tagline")}</div>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[2.5rem] sm:text-7xl lg:text-8xl max-w-4xl">
          <span className="gold-text drop-shadow-[0_0_24px_rgba(244,215,122,0.45)]">{t("hero.main")}</span>
        </h1>
        <p className="mt-3 sm:mt-5 text-white/95 font-display tracking-tight text-base sm:text-2xl lg:text-3xl max-w-2xl">
          {t("hero.sub")}
        </p>
        <p className="mt-2 sm:mt-3 text-white/65 text-xs sm:text-sm max-w-xl leading-relaxed">
          {t("hero.support")}
        </p>
      </div>
    </section>
  );
}

// Public free-event submission modal — anyone can submit, admin must approve
function FreeEventModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [imgUploading, setImgUploading] = useState(false);
  const [desc, setDesc] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  // Optional business linkage — search by name; either pick an existing listing OR just type a name
  const [bizQuery, setBizQuery] = useState("");
  const [bizMatches, setBizMatches] = useState([]);
  const [pickedBiz, setPickedBiz] = useState(null); // {id, name} — when a real listing is chosen

  useEffect(() => {
    const q = bizQuery.trim();
    if (pickedBiz || q.length < 2) { setBizMatches([]); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      axios.get(`${API}/businesses?slim=true&q=${encodeURIComponent(q)}`)
        .then(r => {
          if (cancelled) return;
          const items = Array.isArray(r.data) ? r.data.slice(0, 6) : [];
          setBizMatches(items);
        })
        .catch(() => { if (!cancelled) setBizMatches([]); });
    }, 200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [bizQuery, pickedBiz]);

  if (!open) return null;

  const uploadFile = async (file) => {
    if (!file) return;
    setImgUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      // Public submission — no auth, but use admin proxy is unauthorized
      // For public uploads we send a multipart request to /api/upload-public if exists,
      // but currently /api/upload requires Bearer. Submitter pastes a URL or use a public
      // upload helper — for now we do the standard upload anonymously and gracefully fail.
      const r = await axios.post(`${API}/upload-public`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImgUrl(r.data.url);
    } catch (e) { setMsg("Upload failed — try a direct image URL instead"); }
    setImgUploading(false);
  };

  const submit = async () => {
    setMsg(""); setBusy(true);
    try {
      const r = await axios.post(`${API}/happening/submit-public`, {
        submitter_email: email.trim().toLowerCase(),
        submitter_name: name.trim(),
        image: imgUrl.trim(),
        description: desc.trim(),
        website: website.trim() || null,
        business_id: pickedBiz?.id || null,
        business_name: pickedBiz ? pickedBiz.name : (bizQuery.trim() || null),
      });
      console.log("[FreeEvent] submitted:", r.data);
      setDone(true);
    } catch (e) {
      console.error("[FreeEvent] submit failed:", e, e?.response?.data);
      const detail = e?.response?.data?.detail;
      setMsg(typeof detail === "string" ? detail : (e?.message || "Failed to submit — please try again or use a different email."));
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="free-event-modal" onClick={(e) => e.stopPropagation()} className="bg-bg border border-gold/40 rounded-sm max-w-xl w-full p-7 my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-serif-accent text-gold text-sm">~ Free for everyone</div>
            <div className="font-display uppercase text-2xl tracking-tight">Post your event</div>
            <div className="text-muted text-xs mt-1">Goes to admin review · publishes within 24h · auto-expires Monday 2am PT</div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-white text-2xl leading-none">×</button>
        </div>

        {done ? (
          <div data-testid="free-event-success" className="text-center py-8">
            <div className="text-gold text-3xl mb-2">✓</div>
            <div className="font-display uppercase text-lg">Submitted</div>
            <div className="text-muted text-sm mt-2">We'll review and post within 24 hours. Check Happening This Week for your event.</div>
            <button onClick={onClose} className="mt-6 font-display uppercase text-xs tracking-[0.2em] py-2.5 px-6 bg-gold-grad text-bg rounded-sm">Close</button>
          </div>
        ) : (
          <div className="space-y-3">
            <input data-testid="fe-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email" className="w-full bg-black border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
            <input data-testid="fe-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="w-full bg-black border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
            <input data-testid="fe-image" value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="Image URL (paste any image link)" className="w-full bg-black border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
            <div className="flex items-center gap-2">
              <label className="flex-1 cursor-pointer text-center text-xs font-display uppercase tracking-[0.2em] py-2 border border-white/15 rounded-sm hover:border-gold hover:text-gold">
                {imgUploading ? "Uploading…" : "or upload from device"}
                <input type="file" accept="image/*" onChange={(e) => uploadFile(e.target.files?.[0])} className="hidden" />
              </label>
            </div>
            <div className="text-[10px] text-muted font-serif-accent">📐 Square 1:1 · recommended 1200×1200 · max 5 MB</div>
            {imgUrl && <div className="aspect-square rounded-sm bg-cover bg-center border border-white/10" style={{ backgroundImage: `url('${imgUrl}')` }} />}
            <textarea data-testid="fe-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="What's the event? (date, time, location, vibe)" className="w-full bg-black border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />
            <input data-testid="fe-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="Website / Instagram / ticket link (optional)" className="w-full bg-black border border-white/10 p-3 rounded-sm text-sm focus:border-gold/60 outline-none" />

            {/* Optional business listing — either pick an existing listing (auto-links) or leave blank */}
            <div className="border border-white/10 rounded-sm p-3 bg-black/50 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Business listing (optional)</div>
              {pickedBiz ? (
                <div className="flex items-center justify-between gap-2 text-sm">
                  <div>
                    <span className="text-gold">✓ Linked to </span>
                    <span data-testid="fe-picked-biz" className="text-white">{pickedBiz.name}</span>
                  </div>
                  <button type="button" onClick={() => { setPickedBiz(null); setBizQuery(""); }} className="text-[10px] uppercase tracking-[0.2em] font-display text-muted hover:text-white border border-white/10 hover:border-gold/40 px-2 py-1 rounded-sm">Change</button>
                </div>
              ) : (
                <>
                  <input data-testid="fe-biz-search" value={bizQuery} onChange={(e) => setBizQuery(e.target.value)} placeholder="Start typing your business name…" className="w-full bg-black border border-white/10 p-2.5 rounded-sm text-sm focus:border-gold/60 outline-none" />
                  {bizMatches.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border border-white/10 rounded-sm divide-y divide-white/5">
                      {bizMatches.map(b => (
                        <button key={b.id} type="button" data-testid={`fe-biz-match-${b.id}`} onClick={() => { setPickedBiz({ id: b.id, name: b.name }); setBizQuery(b.name); setBizMatches([]); }} className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm">
                          <div>{b.name}</div>
                          {b.category && <div className="text-[10px] uppercase tracking-widest text-muted">{b.category}{b.subcategory ? ` · ${b.subcategory}` : ""}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="text-[10px] text-muted font-serif-accent">Not on Zip City yet? No problem — just type the business name and skip picking a match.</div>
                </>
              )}
            </div>

            {msg && <div data-testid="fe-error" className="p-3 border border-red-500/40 bg-red-500/10 rounded-sm text-red-300 text-sm font-serif-accent">{msg}</div>}

            <button data-testid="fe-submit" onClick={submit} disabled={busy || !email || !imgUrl || !desc} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-40">
              {busy ? "Submitting…" : "Submit for review"}
            </button>
            <div className="text-[10px] text-muted text-center">Limit 5 submissions per email per week.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// Auto-flipping advertising wheel — 6 paid slots, rotates every 8s
function CarouselWheel({ businesses, onOpenBusiness }) {
  const [slots, setSlots] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchRef = useRef({ x: 0, t: 0, swiping: false });

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/carousel-ads/active`)
      .then(r => { if (alive) setSlots(r.data || []); })
      .catch(() => {});
    load();
    const refresh = setInterval(load, 60000);
    return () => { alive = false; clearInterval(refresh); };
  }, []);

  useEffect(() => {
    if (paused || slots.length === 0) return;
    const t = setInterval(() => setIdx(i => (i + 1) % slots.length), 5000);
    return () => clearInterval(t);
  }, [paused, slots.length]);

  const cur = slots[idx];

  // Fire ad_impression event ONCE per slot per session — the carousel auto-rotates
  // every few seconds so without this dedupe, sitting on the homepage for 10 minutes
  // would fire ~120 impressions for the same paid ad and overcount analytics 10×.
  useEffect(() => {
    if (!cur) return;
    const key = `carousel_${cur.slot || cur.id || idx}`;
    if (__firedAdKeys.has(key)) return;
    __firedAdKeys.add(key);
    axios.post(`${API}/analytics/track`, { event: "ad_impression", key }).catch(() => {});
  }, [cur?.id, cur?.slot, idx]);

  if (!slots || slots.length === 0) return null;

  const handleClick = () => {
    if (touchRef.current.swiping) return; // suppress tap during swipe
    if (!cur) return;
    // Track click for analytics
    axios.post(`${API}/analytics/track`, { event: "misc", key: `carousel_click_${cur.slot || ""}` }).catch(() => {});
    if (cur.business_id) {
      const b = businesses.find(x => x.id === cur.business_id);
      if (b) { onOpenBusiness(b); return; }
    }
    if (cur.cta_url) window.open(cur.cta_url, "_blank", "noopener,noreferrer");
  };

  const onTouchStart = (e) => {
    setPaused(true);
    touchRef.current = { x: e.touches[0].clientX, t: Date.now(), swiping: false };
  };
  const onTouchMove = (e) => {
    const dx = e.touches[0].clientX - touchRef.current.x;
    if (Math.abs(dx) > 6) touchRef.current.swiping = true;
  };
  const onTouchEnd = (e) => {
    const dx = (e.changedTouches[0].clientX - touchRef.current.x);
    const dt = Date.now() - touchRef.current.t;
    if (Math.abs(dx) > 40 && dt < 800 && slots.length > 1) {
      setIdx(i => (dx < 0 ? (i + 1) : (i - 1 + slots.length)) % slots.length);
    }
    setTimeout(() => { touchRef.current.swiping = false; setPaused(false); }, 100);
  };

  return (
    <section data-testid="carousel-wheel" className="relative bg-gradient-to-b from-bg via-black to-bg border-b border-gold/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-3">
          <div className="font-serif-accent text-gold text-sm tracking-wide">~ The Marquee — paid placements</div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1.5">
              {slots.map((_, i) => (
                <button
                  key={i}
                  data-testid={`carousel-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-gold w-6" : "bg-white/30 hover:bg-white/60"}`}
                  aria-label={`Slot ${i + 1}`}
                />
              ))}
            </div>
            <div className="text-[10px] text-muted font-display uppercase tracking-widest">{idx + 1} / {slots.length}</div>
          </div>
        </div>

        <div
          data-testid="carousel-card"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onClick={handleClick}
          className="relative cursor-pointer rounded-sm overflow-hidden border border-gold/30 bg-bg/40 group select-none"
          style={{ aspectRatio: "16/9", touchAction: "pan-y" }}
        >
          {cur.status === "open" ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gold/15 via-transparent to-fuchsia-500/15">
              <div className="text-center px-6">
                <div className="font-serif-accent text-gold text-lg mb-2">~ Premium slot — open</div>
                <div className="font-display uppercase text-2xl sm:text-3xl tracking-tight">Your business here</div>
                <div className="text-muted text-sm mt-2 max-w-md mx-auto">Hero carousel placement · 2-week run · auto-flips with the wheel.</div>
              </div>
            </div>
          ) : (
            <>
              {cur.image_url && (
                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{ backgroundImage: `url('${cur.image_url}')` }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/60 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 px-6 sm:px-10 py-5 sm:py-6 flex items-end justify-between gap-4">
                <div className="max-w-[70%] sm:max-w-2xl min-w-0">
                  <div className="font-serif-accent text-gold text-[10px] sm:text-xs uppercase tracking-[0.3em]">
                    {cur.status === "seed" ? "Featured" : "Sponsored"}
                  </div>
                  <div className="font-display uppercase text-xl sm:text-3xl md:text-4xl tracking-tight mt-1 line-clamp-2">{cur.headline}</div>
                  {cur.body && <div className="text-white/80 text-xs sm:text-sm mt-1 sm:mt-2 line-clamp-2">{cur.body}</div>}
                </div>
                <div className="shrink-0 inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-gold-grad text-bg rounded-sm font-display uppercase text-[10px] sm:text-xs tracking-[0.2em] group-hover:brightness-110 transition shadow-lg shadow-gold/20">
                  {cur.cta_text || "Visit"} →
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// Premium banner-ad slot (half height, vivid color, animated, opens lineup modal on tap)
function BannerAdSlot({ slot, slotData, businesses, onOpenBusiness }) {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const theme = SLOT_THEMES[slot] || SLOT_THEMES.new_deals;
  const meta = slotData?.meta || {};
  const lineup = (slotData?.lineup || []).slice(0, 6);
  const current = slotData?.current;
  const advertiserCount = lineup.length;
  // Track impression once per slot mount (when there's an active paid ad)
  useEffect(() => {
    if (current?.id) {
      const _k = `banner_${slot}_${current.id}`;
      if (__firedAdKeys.has(_k)) return;
      __firedAdKeys.add(_k);
      axios.post(`${API}/analytics/track`, { event: "ad_impression", key: `banner_${slot}` }).catch(() => {});
    }
  }, [slot, current?.id]);
  // Reset to first ad whenever modal re-opens
  useEffect(() => { if (open) setIdx(0); }, [open]);
  const prev = () => setIdx(i => (i - 1 + lineup.length) % Math.max(1, lineup.length));
  const next = () => setIdx(i => (i + 1) % Math.max(1, lineup.length));
  // Touch-swipe (left/right)
  const touchRef = useRef(0);
  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchRef.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next() : prev());
  };
  const activeAd = lineup[idx] || current;
  const activeBiz = activeAd ? businesses.find(x => x.id === activeAd.business_id) : null;

  if (!current && advertiserCount === 0) {
    // Render an "open slot" CTA — entices businesses to advertise here
    return (
      <section data-testid={`banner-empty-${slot}`} className="relative h-24 sm:h-28 overflow-hidden border-y" style={{ borderColor: theme.glow.replace("0.55", "0.4") }}>
        <div className="absolute inset-0 opacity-90" style={{ background: `linear-gradient(90deg, ${theme.from}, ${theme.to})` }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_60%)]" />
        <div className="absolute inset-0 flex items-center justify-between px-5 lg:px-10">
          <div className="text-bg">
            <div className="font-serif-accent italic text-xs sm:text-sm">{meta.tag || "Premium"}</div>
            <div className="font-display uppercase text-xl sm:text-3xl tracking-tight leading-none">{meta.label || slot.replace("_", " ")}</div>
          </div>
          <div className="text-bg text-right">
            <div className="font-display uppercase text-[10px] sm:text-xs tracking-[0.22em] opacity-70">6 spots open</div>
            <div className="font-display uppercase text-sm sm:text-base tracking-tight font-bold">$99 / 4 weeks → Claim 1 of 6</div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <motion.button
        data-testid={`banner-slot-${slot}`}
        onClick={() => setOpen(true)}
        initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        whileHover={{ scale: 1.005 }}
        className="relative h-24 sm:h-28 w-full overflow-hidden block group"
        style={{ boxShadow: `inset 0 0 0 2px ${theme.glow}` }}
      >
        <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${theme.from}, ${theme.to})` }} />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.25),transparent_55%)] mix-blend-overlay" />
        <div className="absolute inset-y-0 -left-1/3 w-1/3 bg-white/20 skew-x-12 banner-shimmer" />
        <div className="absolute inset-0 flex items-center px-5 lg:px-10">
          <div className="flex-1 text-left text-bg">
            <div className="font-serif-accent italic text-xs sm:text-sm opacity-90">{meta.tag || "Featured"}</div>
            <div className="font-display uppercase text-xl sm:text-3xl tracking-tight leading-none drop-shadow">
              {meta.label || slot.replace("_", " ")}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex -space-x-3">
              {lineup.slice(0, 6).map((b, i) => (
                <div key={i} className="w-9 h-9 sm:w-11 sm:h-11 rounded-full border-2 border-bg bg-cover bg-center"
                     style={{ backgroundImage: `url('${b.image_url || ""}')`, zIndex: 6 - i }} />
              ))}
            </div>
            <div className="hidden sm:flex flex-col items-end text-bg">
              <span className="font-display uppercase text-[10px] tracking-[0.22em] opacity-80">{advertiserCount} of 6 spots</span>
              <span className="font-display uppercase text-xs tracking-[0.22em] font-bold">Tap to browse →</span>
            </div>
          </div>
        </div>
      </motion.button>

      <AnimatePresence>
        {open && activeAd && (
          <motion.div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
            <motion.div
              data-testid={`banner-lineup-${slot}`}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              className="relative w-full max-w-lg bg-surface border-2 rounded-sm overflow-hidden max-h-[88vh] overflow-y-auto"
              style={{ borderColor: theme.from }}
            >
              <div className="relative h-20 overflow-hidden" style={{ background: `linear-gradient(90deg, ${theme.from}, ${theme.to})` }}>
                <div className="absolute inset-0 flex items-center px-5 pointer-events-none">
                  <div className="text-bg">
                    <div className="font-serif-accent italic text-xs opacity-90">{meta.tag}</div>
                    <div className="font-display uppercase text-2xl tracking-tight leading-none">{meta.label}</div>
                  </div>
                </div>
                <button data-testid={`banner-lineup-close-${slot}`} type="button" onClick={() => setOpen(false)} className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/70 hover:bg-bg flex items-center justify-center text-white z-20 shadow-lg">✕</button>
              </div>

              <motion.div key={idx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }}>
                {activeAd.image_url && (
                  <div className="aspect-square w-full bg-cover bg-center" style={{ backgroundImage: `url('${activeAd.image_url}')` }} />
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <div className="inline-block font-display uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 rounded-sm" style={{ background: theme.from, color: "#0a0a0a" }}>
                      {idx + 1} of {Math.max(1, advertiserCount)}
                    </div>
                    {activeAd.starts_at && new Date(activeAd.starts_at) > new Date() && (
                      <div className="font-display uppercase text-[10px] tracking-[0.22em] text-amber-300">Queued · starts {new Date(activeAd.starts_at).toLocaleDateString()}</div>
                    )}
                  </div>
                  <div className="font-display uppercase text-2xl tracking-tight">{activeAd.headline || activeBiz?.name}</div>
                  <p className="text-white/85 text-sm mt-2 leading-relaxed">{activeAd.body || activeBiz?.desc}</p>

                  <div className="mt-5 flex gap-2 flex-wrap">
                    {activeBiz && (
                      <button data-testid={`banner-lineup-open-biz-${idx}`} onClick={() => { setOpen(false); onOpenBusiness(activeBiz); }}
                        className="flex-1 font-display uppercase tracking-[0.18em] text-xs py-3 rounded-sm hover:brightness-110"
                        style={{ background: `linear-gradient(90deg, ${theme.from}, ${theme.to})`, color: "#0a0a0a" }}>
                        View {activeBiz.name} →
                      </button>
                    )}
                    {activeAd.cta_url && (
                      <a data-testid={`banner-lineup-cta-${idx}`} href={activeAd.cta_url} target="_blank" rel="noreferrer"
                        className="font-display uppercase tracking-[0.18em] text-xs py-3 px-4 border border-white/15 hover:border-gold hover:text-gold rounded-sm">
                        {activeAd.cta_text || "Visit"} ↗
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>

              {advertiserCount > 1 && (
                <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-2 pointer-events-none">
                  <button data-testid={`banner-prev-${slot}`} onClick={prev}
                    className="pointer-events-auto w-10 h-10 rounded-full bg-black/70 backdrop-blur hover:bg-bg text-white flex items-center justify-center shadow-lg">‹</button>
                  <button data-testid={`banner-next-${slot}`} onClick={next}
                    className="pointer-events-auto w-10 h-10 rounded-full bg-black/70 backdrop-blur hover:bg-bg text-white flex items-center justify-center shadow-lg">›</button>
                </div>
              )}

              {/* Dot pager */}
              {advertiserCount > 1 && (
                <div className="flex items-center justify-center gap-1.5 py-3 border-t border-white/10">
                  {lineup.map((_, i) => (
                    <button key={i} data-testid={`banner-dot-${slot}-${i}`} onClick={() => setIdx(i)}
                      className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6" : "w-1.5 opacity-40"}`}
                      style={{ background: theme.from }} />
                  ))}
                </div>
              )}

              <div className="px-6 py-4 border-t border-white/10 text-center">
                <div className="font-serif-accent italic text-muted text-xs">Want your business here?</div>
                <div className="font-display uppercase text-sm tracking-tight mt-1" style={{ color: theme.from }}>
                  ${(slotData?.meta?.price) || 99} buys 4 weeks · {slotData?.spots_open ?? Math.max(0, 6 - advertiserCount)} of 6 spots left
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function CategoryBar({ cat, index, onClick }) {
  const { lang } = useI18n();
  // First 6 are above-the-fold on mobile → eager-load via <img preload> (already
  // wired in index.html). Later cards use a real <img loading="lazy"> so the
  // browser skips the network entirely until they scroll into view. Cloudinary
  // fetch URL adds edge caching + auto-WebP for ~50% smaller bytes.
  return (
    <button data-testid={`category-bar-${cat.key}`} onClick={() => onClick(cat.key)}
      className="cat-bar group relative h-56 w-full overflow-hidden block text-left border-b border-white/5"
      style={{ animationDelay: `${index * 40}ms` }}>
      <img
        src={cdnUrl(cat.img, "hero")}
        alt={cat.title}
        loading={index < 6 ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={index < 2 ? "high" : "auto"}
        className="cat-img absolute inset-0 w-full h-full object-cover"
        onError={(e) => {
          // Cloudinary fetch can fail if the account hasn't whitelisted the source
          // domain. Fall back to the original URL so the user always sees an image.
          // Only swap once — the empty src guard prevents an infinite loop if the
          // original URL is also broken.
          if (e.currentTarget.dataset.fallback !== "1" && cat.img) {
            e.currentTarget.dataset.fallback = "1";
            e.currentTarget.src = cat.img;
          } else {
            e.currentTarget.style.display = "none";
          }
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10 group-hover:from-black/95 transition-all" />
      <div className="absolute inset-0 flex items-end p-6 lg:p-10">
        <div className="flex items-end justify-between w-full">
          <div>
            <div className="font-serif-accent text-gold text-sm lg:text-base mb-1">{tCatTag(cat, lang)}</div>
            <div className="font-display uppercase text-3xl lg:text-5xl tracking-tight text-white drop-shadow-xl">{tCatTitle(cat, lang)}</div>
          </div>
          <div className="hidden sm:flex items-center gap-2 font-display uppercase text-[10px] tracking-[0.3em] text-gold opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 transition-all duration-500">
            Explore <span className="w-8 h-px bg-gold" />
          </div>
        </div>
      </div>
    </button>
  );
}

function AgeGate({ open, onConfirm, onDeny }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <motion.div data-testid="age-gate" initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }} className="w-full max-w-sm bg-surface border border-gold/40 rounded-sm overflow-hidden text-center">
            <div className="px-7 py-9">
              <div className="font-serif-accent text-gold text-base">~ Adults only</div>
              <div className="font-display uppercase text-3xl tracking-tight gold-text mt-1">21+ Verification</div>
              <div className="text-muted text-sm leading-relaxed mt-4">
                The next section contains content for adults 21 and over. By entering you confirm you are of legal age in your state.
              </div>
              <div className="flex flex-col gap-2 mt-7">
                <button data-testid="age-gate-yes" onClick={onConfirm} className="font-display uppercase tracking-[0.2em] text-xs py-3.5 bg-gold-grad text-bg rounded-sm hover:brightness-110">I'm 21 or older — Enter</button>
                <button data-testid="age-gate-no" onClick={onDeny} className="font-display uppercase tracking-[0.2em] text-[10px] py-3 text-muted hover:text-white">No, take me back</button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function DealPopup({ cat, onClose, onEnter, onGoToBusiness, businesses, user, onLogin }) {
  const [paidAd, setPaidAd] = useState(null);
  const [leadStatus, setLeadStatus] = useState(null); // {redemption_code, offer_text}
  const [leadBusy, setLeadBusy] = useState(false);
  const { lang } = useI18n();
  useEffect(() => {
    if (!cat) return;
    setPaidAd(null); setLeadStatus(null);
    axios.get(`${API}/popup-ads/active/${cat.key}`).then(r => {
      setPaidAd(r.data || null);
      if (r.data?.business_id) {
        // Track popup paid-ad impression (once per category per session)
        const _k = `popup_${cat.key}`;
        if (!__firedAdKeys.has(_k)) {
          __firedAdKeys.add(_k);
          axios.post(`${API}/analytics/track`, { event: "ad_impression", key: _k }).catch(() => {});
        }
      }
    }).catch(() => {});
  }, [cat]);

  const claimLead = async () => {
    if (!paidAd || !paidAd.business_id) return;
    if (!user) return onLogin?.();
    setLeadBusy(true);
    try {
      const r = await axios.post(`${API}/ads/lead/${paidAd.business_id}/${paidAd.category}`, {});
      setLeadStatus(r.data);
    } catch (e) {
      setLeadStatus({ error: e.response?.data?.detail || "Could not claim" });
    }
    setLeadBusy(false);
  };

  if (!cat) return null;

  const openBiz = () => {
    if (!paidAd) return;
    if (paidAd.house) return; // house popup has phone CTA, no business
    const biz = businesses.find(b => b.id === paidAd.business_id);
    if (biz && onGoToBusiness) onGoToBusiness(biz);
  };

  const img = paidAd?.image_url || cat.img;
  const headline = paidAd?.headline || tCatTitle(cat, lang);
  const subtext = paidAd?.body || "Hand-picked deals for Zip City insiders. Skip the line, claim the discount.";
  const offerTag = paidAd?.offer || "Exclusive tonight ~";
  const isHouse = !!paidAd?.house;

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
        <motion.div data-testid="deal-popup" onClick={(e) => e.stopPropagation()} initial={{ y: 40, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 40, opacity: 0, scale: 0.96 }} transition={{ type: "spring", damping: 22, stiffness: 260 }} className="relative w-full max-w-md bg-surface border border-gold/30 rounded-sm overflow-hidden marquee-glow">
          <button
            data-testid="popup-image-btn"
            onClick={paidAd && !isHouse ? openBiz : undefined}
            disabled={!paidAd || isHouse}
            className={`aspect-square w-full bg-cover bg-center relative block ${paidAd && !isHouse ? "cursor-pointer group" : "cursor-default"}`}
            style={{ backgroundImage: `url('${img}')` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/10 to-transparent" />
            {paidAd && !isHouse && (
              <div className="absolute bottom-3 left-3 right-12 bg-black/60 backdrop-blur text-white font-display uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity">
                → Visit {paidAd.business_name}
              </div>
            )}
          </button>
          <button data-testid="popup-close-btn" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur hover:bg-bg text-white flex items-center justify-center z-10">✕</button>
          <div className="p-6 lg:p-8">
            {paidAd && !isHouse && <div className="inline-block mb-2 font-display uppercase text-[10px] tracking-[0.22em] px-2 py-1 bg-gold text-bg rounded-sm">Featured · {paidAd.business_name}</div>}
            {isHouse && <div className="inline-block mb-2 font-display uppercase text-[10px] tracking-[0.22em] px-2 py-1 bg-pink-500 text-white rounded-sm">After-Hours Hotline</div>}
            <div className="font-serif-accent text-gold text-lg">{offerTag}</div>
            <div className="font-display uppercase text-3xl tracking-tight mt-1">{headline}</div>
            <div className="text-muted mt-3 leading-relaxed">{subtext}</div>
            {isHouse && paidAd?.cta_url && (
              <a data-testid="house-popup-call" href={paidAd.cta_url} className="mt-5 block w-full text-center font-display uppercase tracking-[0.2em] text-sm py-4 bg-pink-500 text-white rounded-sm hover:brightness-110 active:scale-[0.98] transition-all">
                {paidAd.cta_text}
              </a>
            )}

            {paidAd?.lead_capture_enabled && !leadStatus && (
              <button
                data-testid="popup-lead-google-btn"
                onClick={claimLead}
                disabled={leadBusy}
                className="mt-5 w-full flex items-center justify-center gap-3 py-4 bg-white text-bg rounded-sm font-display uppercase tracking-[0.18em] text-xs hover:brightness-95 active:scale-[0.98] transition-all disabled:opacity-60"
              >
                <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16 4 9.1 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.1 0-9.5-3.3-11.2-7.9l-6.5 5C9 39.6 16 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.3-.1-2.4-.4-3.5z"/></svg>
                {leadBusy ? "…" : (user ? "Claim with Google · Save Code" : "Continue with Google to Claim")}
              </button>
            )}
            {paidAd?.lead_offer_text && !leadStatus && (
              <div className="text-center text-muted text-xs font-serif-accent mt-2">{paidAd.lead_offer_text}</div>
            )}
            {leadStatus?.redemption_code && (
              <div data-testid="popup-lead-success" className="mt-5 border border-gold/40 rounded-sm p-4 bg-gradient-to-br from-gold/15 to-transparent text-center">
                <div className="font-serif-accent text-gold text-xs uppercase tracking-[0.22em]">{leadStatus.already_claimed ? "Already claimed" : "Saved to your inbox"}</div>
                <div className="font-display gold-text text-3xl tracking-tight my-2">{leadStatus.redemption_code}</div>
                <div className="text-muted text-xs">{leadStatus.offer_text || "Show this code at the venue"}</div>
              </div>
            )}
            {leadStatus?.error && <div className="text-red-400 text-xs mt-3 font-serif-accent">{leadStatus.error}</div>}

            <button data-testid="popup-enter-btn deal-popup-enter" onClick={onEnter} className="mt-3 w-full font-display uppercase tracking-[0.2em] text-sm py-4 bg-gold-grad text-bg rounded-sm hover:brightness-110 active:scale-[0.98] transition-all">Enter {tCatTitle(cat, lang)}</button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function BusinessGrid({ items, onSelect }) {
  if (!items.length) return <div className="text-muted text-center py-20 font-serif-accent text-xl">Nothing here yet — be the first to list.</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((b, i) => (
        <motion.button key={b.id} data-testid={`biz-card-${b.id}`} onClick={() => onSelect(b)}
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          // Cap the cascade so the last card on a 30-item page doesn't wait
          // 1.2s before fading in. We stagger only the first 6 (above the fold).
          transition={{ delay: Math.min(i, 6) * 0.04 }}
          className="group text-left bg-surface border border-white/5 rounded-sm overflow-hidden hover:border-gold/40 transition-all">
          <div className="aspect-square overflow-hidden relative">
            {/* Native <img> with browser lazy-loading — only fetches when the card
                is about to be visible. For a 30-item category on mobile, the
                user no longer downloads all 30 images upfront. Cloudinary
                fetch URL adds edge caching + WebP/AVIF auto-format for ~50%
                smaller payloads. */}
            <img src={cdnUrl(b.img, "card")} alt={b.name}
                 loading={i < 3 ? "eager" : "lazy"}
                 decoding="async"
                 className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                 onError={(e) => {
                   // Cloudinary fetch failed (likely fetch-allow-list not configured).
                   // Swap to the original URL once so the image still shows.
                   if (e.currentTarget.dataset.fallback !== "1" && b.img) {
                     e.currentTarget.dataset.fallback = "1";
                     e.currentTarget.src = b.img;
                   } else {
                     e.currentTarget.style.display = "none";
                   }
                 }} />
            <div className="absolute top-3 left-3 bg-gold-grad text-bg text-[10px] tracking-[0.2em] uppercase font-display px-2.5 py-1 rounded-sm">{b.offer}</div>
            {b.subcategory && <div className="absolute top-3 right-3 bg-bg/70 backdrop-blur text-gold text-[10px] tracking-[0.18em] uppercase font-display px-2 py-1 rounded-sm border border-gold/20">{b.subcategory}</div>}
          </div>
          <div className="p-5">
            <div className="font-display text-xl tracking-tight">{b.name}</div>
            <div className="font-serif-accent text-gold text-sm mt-0.5">★ {(b.rating||4.7).toFixed(1)} · {b.reviews||200} reviews</div>
            <div className="text-muted text-sm mt-3 leading-relaxed line-clamp-2">{b.desc}</div>
          </div>
        </motion.button>
      ))}
    </div>
  );
}

function BusinessDetail({ biz: bizProp, currentCategory, onClose }) {
  const [ad, setAd] = useState(null);
  const [adDismissed, setAdDismissed] = useState(false);
  const [fullBiz, setFullBiz] = useState(bizProp);
  const { lang } = useI18n();
  useEffect(() => {
    // Reset all internal state when the modal is dismissed (bizProp -> null).
    // Without resetting fullBiz, the modal would remain visible after onClose()
    // because the render falls back to the cached fullBiz value.
    if (!bizProp) { setAd(null); setAdDismissed(false); setFullBiz(null); return; }
    setAd(null); setAdDismissed(false);
    setFullBiz(bizProp);
    // Always refresh the biz doc from the API when the modal opens. The
    // homepage/category grids ship a SLIM shape that omits `website` and other
    // "full-detail" fields for payload speed — without this fetch the modal
    // silently rendered without the website card + description, even when
    // the backend had them saved.
    axios.get(`${API}/businesses/${bizProp.id}`).then(r => setFullBiz(r.data)).catch(() => {});
    // fire-and-forget business view tracking for owner analytics
    axios.post(`${API}/businesses/${bizProp.id}/track-view`).catch(() => {});
    const q = currentCategory ? `?category=${currentCategory}` : "";
    axios.get(`${API}/ads/${bizProp.id}${q}`).then(r => {
      setAd(r.data);
      // Dedupe site-wide ad impressions per (biz, category) per session — without
      // this, opening the same listing twice would double the counter.
      const _adKey = `247_${bizProp.id}_${r.data.category}`;
      if (!__firedAdKeys.has(_adKey)) {
        __firedAdKeys.add(_adKey);
        axios.post(`${API}/ads/track/${bizProp.id}/${r.data.category}/impression`).catch(() => {});
        axios.post(`${API}/analytics/track`, { event: "ad_impression", key: `247_${r.data.category}` }).catch(() => {});
      }
    }).catch(() => {});
  }, [bizProp, currentCategory]);

  // Downstream JSX still uses `biz` — alias to fullBiz so lazy-fetched fields render
  const biz = fullBiz || bizProp;

  const trackClick = () => {
    if (ad) axios.post(`${API}/ads/track/${biz.id}/${ad.category}/click`).catch(() => {});
  };

  const shareBiz = async () => {
    if (!biz) return;
    // Branded share URL: backend serves OG tags here. Crawlers (iMessage/WA/FB)
    // fetch this URL and render a rich preview; humans get redirected to the
    // SPA at the public site domain (PUBLIC_SITE_URL on backend).
    const url = `${API}/share/biz/${biz.id}`;
    const text = `${biz.name} — ${biz.offer || "Check this out on ZipCityLV"}\n${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: biz.name, text: biz.name, url });
      } else {
        await navigator.clipboard.writeText(text);
        alert("Link copied to clipboard");
      }
    } catch (e) { /* user cancelled */ }
  };

  if (!biz || !bizProp) return null;
  const mapsUrl = `https://maps.google.com/?q=${encodeURIComponent(biz.address)}`;
  const uberUrl = `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(biz.address)}`;
  const lyftUrl = `https://lyft.com/ride?id=lyft&destination[address]=${encodeURIComponent(biz.address)}`;
  const adPhoneClean = (ad?.phone || "").replace(/[^0-9+]/g, "");
  const adPhoneDisplay = ad?.phone || "";

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-black/90 backdrop-blur-xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
        <motion.div data-testid="business-detail" onClick={(e) => e.stopPropagation()} initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ type: "spring", damping: 26, stiffness: 240 }} className="w-full max-w-xl bg-surface border border-white/10 rounded-sm overflow-hidden max-h-[90vh] overflow-y-auto">
          <div className="aspect-square w-full bg-cover bg-center relative" style={{ backgroundImage: `url('${biz.img}')` }}>
            <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" />
            <button data-testid="biz-detail-share" onClick={shareBiz} className="absolute top-3 right-14 w-9 h-9 rounded-full bg-black/60 backdrop-blur hover:bg-gold hover:text-bg text-white flex items-center justify-center transition-colors" aria-label="Share">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            </button>
            <button data-testid="biz-detail-close" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 backdrop-blur hover:bg-bg flex items-center justify-center">✕</button>
            <div className="absolute bottom-3 left-3 bg-gold-grad text-bg font-display uppercase text-[10px] tracking-[0.22em] px-3 py-1.5 rounded-sm">{biz.offer}</div>
          </div>
          <div className="p-6 md:p-8">
            <div className="font-display text-3xl uppercase tracking-tight">{biz.name}</div>
            <div className="font-serif-accent text-gold mt-1">★ {(biz.rating||4.7).toFixed(1)} · {biz.reviews||200} reviews {biz.subcategory ? `· ${biz.subcategory}` : ""}</div>

            {ad && !adDismissed && (
              <motion.div
                data-testid="biz-spotlight"
                initial={{ scale: 0.92, opacity: 0, y: 16 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                transition={{ type: "spring", damping: 18, stiffness: 240, delay: 0.15 }}
                className="relative mt-5 border-2 border-gold/60 rounded-sm overflow-hidden bg-gradient-to-br from-bg via-surface to-bg ad-247-glow"
              >
                <button
                  data-testid="biz-spotlight-close"
                  onClick={() => setAdDismissed(true)}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 backdrop-blur hover:bg-bg text-white flex items-center justify-center z-20 shadow-lg"
                  aria-label="Close ad"
                >✕</button>
                {ad.image_url && (
                  <div className="aspect-square w-full bg-cover bg-center" style={{ backgroundImage: `url('${ad.image_url}')` }} />
                )}
                <div className="p-5 sm:p-6">
                  <div className="inline-block font-display uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 rounded-sm bg-pink-500 text-white shadow-lg shadow-pink-500/30">
                    24/7 AD · {tCatTitle(CATEGORIES.find(c => c.key === ad.category), lang) || ad.category}
                  </div>
                  <div className="font-display uppercase text-2xl sm:text-3xl tracking-tight mt-3 gold-text drop-shadow-[0_0_12px_rgba(244,215,122,0.35)]">
                    {ad.headline}
                  </div>
                  <p className="text-white/90 text-sm sm:text-base mt-3 leading-relaxed">{ad.body}</p>

                  <div className="mt-5 space-y-2.5">
                    {adPhoneDisplay && (
                      <a
                        data-testid="biz-spotlight-call"
                        href={`tel:${adPhoneClean}`}
                        onClick={trackClick}
                        className="flex items-center justify-center gap-2 w-full font-display uppercase tracking-[0.18em] text-sm py-4 bg-pink-500 text-white rounded-sm hover:brightness-110 active:scale-[0.98] transition-all shadow-lg shadow-pink-500/40"
                      >
                        <span className="text-base">☏</span> CALL {adPhoneDisplay}
                      </a>
                    )}
                    {ad.cta_url && (
                      <a
                        data-testid="biz-spotlight-cta"
                        href={ad.cta_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={trackClick}
                        className="block text-center w-full font-display uppercase tracking-[0.18em] text-sm py-4 bg-gold-grad text-bg rounded-sm hover:brightness-110 active:scale-[0.98] transition-all"
                      >
                        {ad.cta_text || "Visit"} →
                      </a>
                    )}
                  </div>
                  <div className="text-muted text-[10px] uppercase tracking-[0.22em] font-display mt-4 text-center">
                    Updated {new Date(ad.updated_at).toLocaleDateString()}
                  </div>
                </div>
              </motion.div>
            )}

            <a data-testid="biz-detail-maps" href={mapsUrl} target="_blank" rel="noreferrer" className="mt-5 flex gap-4 items-center bg-bg/60 hover:bg-bg p-4 rounded-sm border border-white/5 hover:border-gold/30 transition-all">
              <div className="w-10 h-10 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold">📍</div>
              <div className="text-sm">
                <div className="text-muted uppercase tracking-[0.18em] text-[10px] font-display">Location</div>
                <div className="font-medium">{biz.address}</div>
              </div>
            </a>

            {(biz.phone || biz.website) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {biz.phone && (
                  <a data-testid="biz-detail-phone" href={`tel:${(biz.phone || "").replace(/[^0-9+]/g, "")}`} className="flex gap-3 items-center bg-bg/60 hover:bg-bg p-3.5 rounded-sm border border-white/5 hover:border-gold/30 transition-all">
                    <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold text-sm">☏</div>
                    <div className="text-sm min-w-0">
                      <div className="text-muted uppercase tracking-[0.18em] text-[10px] font-display">Call</div>
                      <div className="font-medium truncate">{biz.phone}</div>
                    </div>
                  </a>
                )}
                {biz.website && (
                  <a data-testid="biz-detail-website" href={/^https?:\/\//i.test(biz.website) ? biz.website : `https://${biz.website}`} target="_blank" rel="noreferrer" className="flex gap-3 items-center bg-bg/60 hover:bg-bg p-3.5 rounded-sm border border-white/5 hover:border-gold/30 transition-all">
                    <div className="w-9 h-9 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold text-sm">↗</div>
                    <div className="text-sm min-w-0">
                      <div className="text-muted uppercase tracking-[0.18em] text-[10px] font-display">Website</div>
                      <div className="font-medium truncate">{biz.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</div>
                    </div>
                  </a>
                )}
              </div>
            )}

            <BizSocialLinks instagram={biz.instagram} tiktok={biz.tiktok} facebook={biz.facebook} x={biz.x} />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <a data-testid="launch-uber" href={uberUrl} target="_blank" rel="noreferrer" className="text-center py-3.5 border border-white/10 hover:border-white/40 rounded-sm font-display uppercase text-xs tracking-[0.2em]">Uber</a>
              <a data-testid="launch-lyft" href={lyftUrl} target="_blank" rel="noreferrer" className="text-center py-3.5 bg-velvet-grad rounded-sm font-display uppercase text-xs tracking-[0.2em]">Lyft</a>
            </div>
            {biz.desc && <p className="mt-6 text-muted leading-relaxed">{biz.desc}</p>}

            <BizEmailCapture businessId={biz.id} businessName={biz.name} />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}


// Renders IG / TikTok / Facebook circular icon links on a business landing
// page. Accepts either a full URL, or a bare handle/page-name and normalizes it
// to the correct public profile URL. Skips any platform with an empty value.
function BizSocialLinks({ instagram, tiktok, facebook, x }) {
  const norm = (v, platform) => {
    if (!v || !v.trim()) return null;
    const s = v.trim();
    if (/^https?:\/\//i.test(s)) return s;
    const handle = s.replace(/^@/, "").replace(/^\//, "");
    if (platform === "instagram") return `https://instagram.com/${handle}`;
    if (platform === "tiktok")    return `https://www.tiktok.com/@${handle}`;
    if (platform === "facebook")  return `https://facebook.com/${handle}`;
    if (platform === "x")         return `https://x.com/${handle}`;
    return s;
  };
  const items = [
    { key: "instagram", label: "Instagram", href: norm(instagram, "instagram"), color: "from-fuchsia-500 via-pink-500 to-orange-400",
      svg: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.06.42 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.42 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.06.37-2.23.42-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.42-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.17-.42-.37-1.06-.42-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.42-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.06-.37 2.23-.42C8.42 2.21 8.8 2.2 12 2.2M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.66.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.66-.66 1.07-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.94 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zm0 10.16a4 4 0 110-8 4 4 0 010 8zm6.4-11.85a1.44 1.44 0 100 2.88 1.44 1.44 0 000-2.88z"/></svg>) },
    { key: "tiktok",    label: "TikTok",    href: norm(tiktok, "tiktok"),    color: "from-cyan-400 via-black to-rose-500",
      svg: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.53.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>) },
    { key: "facebook",  label: "Facebook",  href: norm(facebook, "facebook"), color: "from-blue-600 to-blue-500",
      svg: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>) },
    { key: "x",         label: "X",         href: norm(x, "x"),               color: "from-neutral-900 via-black to-neutral-800 border border-white/20",
      svg: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>) },
  ].filter(x => x.href);
  if (items.length === 0) return null;
  return (
    <div data-testid="biz-detail-socials" className="mt-4 flex flex-wrap gap-3">
      {items.map(it => (
        <a
          key={it.key}
          data-testid={`biz-detail-social-${it.key}`}
          href={it.href}
          target="_blank"
          rel="noreferrer noopener"
          aria-label={it.label}
          className={`w-11 h-11 rounded-full flex items-center justify-center text-white bg-gradient-to-br ${it.color} hover:scale-105 hover:shadow-lg transition-transform`}
          title={it.label}
        >
          {it.svg}
        </a>
      ))}
    </div>
  );
}


// Small "Get updates from <biz>" email capture rendered inside every business
// detail modal. Each business owns its own subscriber list (see backend
// /api/business/subscribe). The owner sees these under Business Portal → Emails.
function BizEmailCapture({ businessId, businessName }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", msg: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setState({ status: "err", msg: "Enter a valid email" });
      return;
    }
    setState({ status: "loading", msg: "" });
    try {
      await axios.post(`${API}/business/subscribe`, { email: email.trim(), business_id: businessId, source: "biz_page" });
      setState({ status: "ok", msg: "✓ You're on the list. We'll let you know when they run a deal." });
      setEmail("");
    } catch (err) {
      setState({ status: "err", msg: err.response?.data?.detail || err.message });
    }
  };
  return (
    <div data-testid="biz-email-capture" className="mt-8 border border-white/10 rounded-sm p-4 bg-bg/40">
      <div className="font-display uppercase text-[11px] tracking-[0.2em] text-gold">Get updates from {businessName}</div>
      <div className="text-xs text-muted mt-1 mb-3 font-serif-accent">Deals, new hours, and event drops — straight to your inbox.</div>
      {state.status === "ok" ? (
        <div data-testid="biz-email-capture-success" className="text-gold text-sm font-serif-accent">{state.msg}</div>
      ) : (
        <form onSubmit={submit} className="flex gap-2">
          <input
            data-testid="biz-email-capture-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 bg-bg border border-white/10 p-2.5 rounded-sm text-sm"
          />
          <button
            data-testid="biz-email-capture-submit"
            disabled={state.status === "loading"}
            className="px-4 py-2 bg-gold-grad text-bg font-display uppercase text-[10px] tracking-[0.2em] rounded-sm hover:brightness-110 disabled:opacity-50"
          >
            {state.status === "loading" ? "…" : "Notify Me"}
          </button>
        </form>
      )}
      {state.status === "err" && <div className="text-red-400 text-xs mt-2 font-serif-accent">{state.msg}</div>}
    </div>
  );
}


// Owner view of the email subscribers for a specific business.
// Includes a one-click "Share business page link" copy-to-clipboard action —
// makes it trivial for the owner to blast their list from their own inbox/CRM.
function BizOwnerEmailList({ businessId }) {
  const [state, setState] = useState({ loading: true, err: "", subscribers: [], count: 0 });
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!businessId) { setState({ loading: false, err: "Select a business first.", subscribers: [], count: 0 }); return; }
    (async () => {
      setState((s) => ({ ...s, loading: true, err: "" }));
      try {
        const sess = localStorage.getItem("zc_session") || "";
        const r = await axios.get(`${API}/business/emails`, {
          params: { business_id: businessId },
          headers: sess ? { Authorization: `Bearer ${sess}` } : {},
        });
        if (!cancelled) setState({ loading: false, err: "", subscribers: r.data.subscribers || [], count: r.data.count || 0 });
      } catch (e) {
        if (!cancelled) setState({ loading: false, err: e.response?.data?.detail || e.message, subscribers: [], count: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  const shareUrl = `${window.location.origin}/?biz=${encodeURIComponent(businessId || "")}`;

  const share = async () => {
    // Prefer the native share sheet on mobile; fall back to clipboard on desktop.
    if (navigator.share) {
      try { await navigator.share({ url: shareUrl, title: "Check out my listing on ZipCityLV" }); return; }
      catch { /* user cancelled or unavailable — fall through to clipboard copy */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  };

  const downloadCsv = () => {
    const rows = [["email", "source", "created_at"]];
    for (const s of state.subscribers) rows.push([s.email, s.source || "", s.created_at || ""]);
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `business-emails-${businessId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (state.loading) return <div data-testid="portal-emails-loading" className="text-muted text-center py-8 font-serif-accent">Loading…</div>;
  if (state.err) return <div data-testid="portal-emails-error" className="text-red-400 text-sm font-serif-accent">{state.err}</div>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="border border-white/10 rounded-sm px-3 py-2 mr-auto">
          <div className="font-display uppercase text-[10px] tracking-[0.2em] text-muted">Subscribers</div>
          <div data-testid="portal-emails-count" className="font-display text-xl">{state.count}</div>
        </div>
        <button
          data-testid="portal-emails-share"
          onClick={share}
          className="font-display uppercase text-[10px] tracking-[0.2em] px-4 py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110"
          title="Share your business page link — perfect for pasting into an email blast"
        >
          {copied ? "✓ Link Copied" : "Share Business Link"}
        </button>
        {state.subscribers.length > 0 && (
          <button
            data-testid="portal-emails-csv"
            onClick={downloadCsv}
            className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-gold/40 text-gold hover:bg-gold hover:text-bg rounded-sm"
          >
            ⬇ CSV
          </button>
        )}
      </div>
      {state.subscribers.length === 0 ? (
        <div data-testid="portal-emails-empty" className="text-muted text-center py-10 border border-white/5 rounded-sm font-serif-accent">
          No subscribers yet. Once visitors submit the "Get updates" form on your business page, they'll show up here.
        </div>
      ) : (
        <div className="border border-white/10 rounded-sm overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 bg-bg/50 text-[10px] uppercase tracking-[0.22em] text-muted font-display">
            <div className="col-span-7">Email</div>
            <div className="col-span-3">Source</div>
            <div className="col-span-2 text-right">Date</div>
          </div>
          {state.subscribers.map(s => (
            <div key={s.id || s.email} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm">
              <div className="col-span-7 truncate">{s.email}</div>
              <div className="col-span-3 text-muted text-xs font-serif-accent">{s.source || "biz_page"}</div>
              <div className="col-span-2 text-right text-muted text-xs font-mono">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// PrepurchaseCard / MyPopupsPrepurchase
// Render a "Reserve next N days at 10% off" CTA when the owner
// has an active paid ad (popup / banner / carousel).
// ─────────────────────────────────────────────────────────────
function MyPopupsPrepurchase({ businessId }) {
  const [popups, setPopups] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/popup-ads/mine/${businessId}`)
      .then(r => { if (!cancelled) setPopups(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [businessId]);
  if (popups.length === 0) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="popup-prepurchase-list">
      {popups.map(p => (
        <PrepurchaseCard key={p.id} adType="popup" adId={p.id} label={`Pop-up · ${p.category}`} testIdPrefix={`popup-prepurchase-${p.category}`} />
      ))}
    </div>
  );
}



function PrepurchaseCard({ adType, adId, label, testIdPrefix = "prepurchase" }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    if (!adId) return;
    axios.get(`${API}/ads/prepurchase/${adType}/${adId}`)
      .then(r => { if (!cancelled) setInfo(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [adType, adId]);
  if (!info) return null;
  if (info.already_prepurchased) {
    const exp = info.current_expiry ? new Date(info.current_expiry) : null;
    return (
      <div data-testid={`${testIdPrefix}-reserved`} className="mt-4 border border-emerald-500/30 rounded-sm p-4 bg-emerald-500/5">
        <div className="font-display uppercase text-xs tracking-[0.2em] text-emerald-300">✓ Next cycle reserved · 10% off applied</div>
        {exp && <div className="text-muted text-xs font-serif-accent mt-1">Runs through {exp.toLocaleDateString()}.</div>}
      </div>
    );
  }
  if (!info.eligible) return null;
  const reserve = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/stripe/ad-prepurchase`, {
        ad_type: adType, ad_id: adId, origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) { setErr(e.response?.data?.detail || "Could not start checkout"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid={`${testIdPrefix}-card`} className="mt-4 border-2 border-gold/40 rounded-sm p-5 bg-gradient-to-br from-gold/10 to-transparent">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <div className="font-serif-accent text-gold text-xs">~ One-time offer · while slot is yours</div>
          <div className="font-display uppercase text-base tracking-tight mt-1">Reserve next {info.duration_days} days — {info.discount_pct}% off</div>
          <div className="text-muted text-xs mt-1">Lock in {label || "this ad"} for an additional {info.duration_days}-day cycle right now. Was <span className="line-through opacity-60">${info.full_price.toFixed(2)}</span> → <span className="text-gold font-display">${info.prepurchase_price.toFixed(2)}</span>. Limit: one extension per ad.</div>
        </div>
        <button data-testid={`${testIdPrefix}-btn`} disabled={busy} onClick={reserve} className="font-display uppercase tracking-[0.2em] text-[11px] py-3 px-5 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50 shadow-lg shadow-gold/20">
          {busy ? "Redirecting…" : `Reserve · $${info.prepurchase_price.toFixed(2)}`}
        </button>
      </div>
      {err && <div className="mt-3 text-red-300 text-xs">{err}</div>}
    </div>
  );
}


function SubcategoryView({ cat, items, onBack, onSelect }) {
  const [sub, setSub] = useState("all");
  const { lang } = useI18n();
  const subs = useMemo(() => Array.from(new Set(items.map((b) => b.subcategory).filter(Boolean))), [items]);
  const filtered = sub === "all" ? items : items.filter((b) => b.subcategory === sub);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button data-testid="subcategory-back subcat-back" onClick={onBack} className="flex items-center gap-2 text-gold hover:gap-3 transition-all font-display uppercase text-xs tracking-[0.22em] mb-6">← Back to Directory</button>
      <div className="font-serif-accent text-gold text-lg mb-2">{tCatTag(cat, lang)}</div>
      <h2 className="font-display uppercase text-5xl lg:text-7xl tracking-tight mb-8">{tCatTitle(cat, lang)}</h2>
      {subs.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-8 pb-2">
          {["all", ...subs].map((s) => {
            const active = sub === s;
            return (
              <button key={s} data-testid={`subcat-chip-${s}`} onClick={() => setSub(s)}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-display uppercase tracking-[0.15em] transition-all ${active ? "bg-gold text-bg" : "border border-white/10 text-muted hover:border-gold/40 hover:text-gold"}`}>
                {s === "all" ? "All" : s}
              </button>
            );
          })}
        </div>
      )}
      <BusinessGrid items={filtered} onSelect={onSelect} />
    </motion.div>
  );
}

const PRICING = [
  { id: "basic",    price: 29, regular: 58,  label: "Basic",    tagline: "1 category listing", features: ["Single category placement", "Editable listing", "14-day trial"] },
  { id: "standard", price: 49, regular: 98,  label: "Standard", tagline: "2 categories", features: ["List in 2 categories", "Priority placement", "14-day trial"] },
  { id: "pro",      price: 99, regular: 198, label: "Pro",      tagline: "4 categories + 20% off every paid ad", features: ["List in 4 categories", "Top priority", "20% off pop-up / banner / carousel ads", "Featured in What's New", "14-day trial"], featured: true },
];

// Happening This Week — weekly-reset event board (Mon-Sun PT)
function HappeningView({ onBack, onPostEvent, onOpenBusiness }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/happening`).then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button data-testid="happening-back" onClick={onBack} className="flex items-center gap-2 text-gold hover:gap-3 transition-all font-display uppercase text-xs tracking-[0.22em] mb-6">← Back to Directory</button>
      <div className="font-serif-accent text-gold text-lg mb-2">~ Mon-Sun · resets weekly ~</div>
      <h2 className="font-display uppercase text-5xl lg:text-7xl tracking-tight mb-2">Happening This Week</h2>
      <p className="text-muted max-w-2xl mb-6 leading-relaxed">Events, concerts, shows and one-week-only specials. Wiped clean every Monday at 2am PT to make way for what's next.</p>

      {/* Post-Event banner — duplicated from the footer CTA so it's visible right where owners scroll for ideas */}
      {onPostEvent && (
        <button
          data-testid="happening-post-event-banner"
          onClick={onPostEvent}
          className="w-full mb-10 group relative overflow-hidden border-2 border-gold/40 bg-gradient-to-r from-gold/10 via-gold/5 to-transparent hover:border-gold hover:from-gold/20 hover:via-gold/10 rounded-sm p-5 sm:p-6 text-left transition-all"
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-display uppercase text-lg sm:text-2xl tracking-tight text-white">Post Event / Specials For Free</div>
              <div className="text-muted text-xs sm:text-sm mt-1 font-serif-accent">Run time: Mon–Sun · auto-clears every Monday at 2am PT</div>
            </div>
            <div className="font-display uppercase text-[10px] tracking-[0.22em] px-4 py-2 rounded-sm bg-gold-grad text-bg group-hover:brightness-110 whitespace-nowrap">Submit →</div>
          </div>
        </button>
      )}

      {loading && <div className="text-muted font-serif-accent">Loading…</div>}
      {!loading && items.length === 0 && (
        <div data-testid="happening-empty" className="border border-white/10 rounded-sm p-10 text-center bg-bg/40">
          <div className="font-serif-accent text-gold text-lg">~ Quiet on the strip ~</div>
          <div className="text-muted mt-2">Nothing posted yet this week. Check back — businesses post here through Sunday night.</div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(ev => {
          const linkedBiz = ev.business_id ? { id: ev.business_id, website: ev.website } : null;
          const imageClickable = !!(linkedBiz && onOpenBusiness);
          return (
            <article key={ev.id} data-testid={`happening-${ev.id}`} className="group border border-white/10 hover:border-gold/40 rounded-sm overflow-hidden bg-surface/40 transition-all">
              {imageClickable ? (
                <button
                  data-testid={`happening-image-${ev.id}`}
                  type="button"
                  onClick={() => onOpenBusiness(ev.business_id)}
                  className="block w-full aspect-square bg-cover bg-center cursor-pointer transition-transform hover:scale-[1.02]"
                  style={{ backgroundImage: `url('${ev.image}')` }}
                  aria-label={`Open ${ev.business_name || 'business'} page`}
                />
              ) : (
                <div className="aspect-square bg-cover bg-center" style={{ backgroundImage: `url('${ev.image}')` }} />
              )}
              <div className="p-5">
                {ev.business_name && (
                  imageClickable ? (
                    <button type="button" data-testid={`happening-bizname-${ev.id}`} onClick={() => onOpenBusiness(ev.business_id)} className="font-serif-accent text-gold text-xs hover:underline">
                      {ev.business_name}
                    </button>
                  ) : (
                    <div className="font-serif-accent text-gold text-xs">{ev.business_name}</div>
                  )
                )}
                <p className="text-white/90 text-sm leading-relaxed mt-1">{ev.description}</p>
                {ev.website && (
                  <a data-testid={`happening-link-${ev.id}`} href={/^https?:\/\//i.test(ev.website) ? ev.website : `https://${ev.website}`} target="_blank" rel="noreferrer" className="inline-block mt-3 font-display uppercase text-[10px] tracking-[0.2em] text-bg bg-gold-grad px-4 py-2 rounded-sm hover:brightness-110">
                    Visit ↗
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </motion.div>
  );
}

// (ImageUploader, CropModal, uploadImage are imported from ./ImageUploader)

// ─── Team-member: forced password reset modal ────────────────────────────
function TeamPasswordResetModal() {
  const { user, teamSetPassword, logout } = useAuth();
  const mustReset = !!(user && user.is_team_member && user.must_reset_password);
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!mustReset) return null;

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (newPw.length < 8) { setErr("New password must be at least 8 characters"); return; }
    if (newPw !== confirmPw) { setErr("Passwords don't match"); return; }
    setBusy(true);
    try {
      await teamSetPassword({ email: user.email, old_password: oldPw, new_password: newPw });
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Could not set password");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl">
      <div data-testid="team-reset-password-modal" className="w-full max-w-sm bg-surface border border-gold/40 rounded-sm overflow-hidden">
        <form onSubmit={submit} className="p-7">
          <div className="font-serif-accent text-gold text-sm">~ First-time setup</div>
          <div className="font-display uppercase text-2xl tracking-tight mt-1">Set a new password</div>
          <div className="text-muted text-sm leading-relaxed mt-3">
            You're signed in with a temporary password. Pick a permanent one to continue.
          </div>

          <label className="block mt-5 text-[11px] uppercase tracking-[0.2em] text-muted">Temporary password</label>
          <input data-testid="team-reset-old-pw" type="password" autoComplete="current-password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="w-full mt-2 bg-bg border border-white/10 px-3.5 py-3 rounded-sm text-sm focus:border-gold/60 outline-none" />

          <label className="block mt-4 text-[11px] uppercase tracking-[0.2em] text-muted">New password (8+ chars)</label>
          <input data-testid="team-reset-new-pw" type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full mt-2 bg-bg border border-white/10 px-3.5 py-3 rounded-sm text-sm focus:border-gold/60 outline-none" />

          <label className="block mt-4 text-[11px] uppercase tracking-[0.2em] text-muted">Confirm new password</label>
          <input data-testid="team-reset-confirm-pw" type="password" autoComplete="new-password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="w-full mt-2 bg-bg border border-white/10 px-3.5 py-3 rounded-sm text-sm focus:border-gold/60 outline-none" />

          {err && <div data-testid="team-reset-error" className="mt-3 text-red-400 text-xs">{err}</div>}

          <button data-testid="team-reset-submit" type="submit" disabled={busy} className="w-full mt-5 font-display uppercase tracking-[0.2em] text-xs py-3.5 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">
            {busy ? "Saving…" : "Save & Continue"}
          </button>
          <button type="button" onClick={logout} className="w-full mt-2 text-xs text-muted hover:text-white py-2">Sign out</button>
        </form>
      </div>
    </div>
  );
}

// ─── Casino Business self-serve plan card (Plans tab) ───────────────────
function CasinoPlanCard({ livePrices }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showForm, setShowForm] = useState(false);

  // Live pricing pulled from /api/plans (admin editable)
  const casino = livePrices?.subscriptions?.casino;
  const price = casino?.price ?? 2499;
  const regular = casino?.regular_price ?? 4998;
  const description = casino?.description || "Enterprise — 5 Pro businesses under one subscription, team-member logins, combined analytics";
  const included = 5;

  // Owner already has a casino sub? Show active state
  const alreadyActive = !!(user && user.casino_subscription_id && !user.is_team_member);
  // Team members shouldn't see the CTA (they can't buy anything)
  const isTeam = !!user?.is_team_member;

  const startCheckout = async () => {
    setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/stripe/casino-subscribe`, {
        origin_url: window.location.origin,
        company_name: companyName.trim(),
      });
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div data-testid="casino-plan-card" className="mt-6 border-2 border-purple-500/50 rounded-sm p-6 bg-gradient-to-br from-purple-500/12 via-gold/5 to-transparent shadow-xl shadow-purple-500/10">
      <div className="flex items-start gap-6 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="inline-block font-display uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 rounded-sm bg-gradient-to-r from-purple-500 to-gold text-bg font-bold">Enterprise · Multi-Property</div>
          <div className="font-display uppercase text-3xl tracking-tight mt-3">Casino Business</div>
          <div className="mt-2 flex items-baseline gap-2">
            <span data-testid="casino-plan-price" className="font-display text-4xl gold-text">${price.toLocaleString()}</span>
            {regular > price && <span className="text-muted text-base line-through opacity-60">${regular.toLocaleString()}</span>}
            <span className="text-muted text-xs uppercase tracking-widest">/mo</span>
          </div>
          <p className="text-muted text-sm mt-2 font-serif-accent leading-relaxed max-w-md">{description}</p>
          <ul className="text-sm text-muted space-y-1.5 my-4 max-w-md">
            <li className="flex gap-2"><span className="text-gold">✦</span> {included} Pro-tier businesses under one master account</li>
            <li className="flex gap-2"><span className="text-gold">✦</span> Individual team-member logins per property (scoped access)</li>
            <li className="flex gap-2"><span className="text-gold">✦</span> Combined analytics dashboard across all properties</li>
            <li className="flex gap-2"><span className="text-gold">✦</span> 4 categories per business + priority claim approval</li>
            <li className="flex gap-2"><span className="text-gold">✦</span> Dedicated onboarding &amp; single monthly invoice</li>
          </ul>
        </div>
        <div className="flex flex-col items-stretch gap-2 min-w-[220px]">
          {alreadyActive && (
            <div data-testid="casino-plan-active-badge" className="border border-gold/50 bg-gold/10 rounded-sm p-4 text-center">
              <div className="font-display uppercase text-[10px] tracking-[0.22em] text-gold">✓ Active</div>
              <div className="text-white text-sm mt-1">Manage your enterprise account from the <span className="text-gold">Casino</span> tab in Business Portal.</div>
            </div>
          )}
          {isTeam && !alreadyActive && (
            <div className="border border-white/10 rounded-sm p-4 text-center text-muted text-xs font-serif-accent">
              Team members can't purchase — ask your account owner.
            </div>
          )}
          {!alreadyActive && !isTeam && !showForm && (
            <button
              data-testid="casino-plan-start-btn"
              onClick={() => setShowForm(true)}
              className="w-full font-display uppercase tracking-[0.2em] text-xs py-3.5 px-6 bg-gradient-to-r from-purple-500 to-gold text-bg rounded-sm hover:brightness-110 disabled:opacity-40 shadow-lg shadow-purple-500/30"
            >
              Start Casino Business
            </button>
          )}
          {!alreadyActive && !isTeam && showForm && (
            <>
              <input
                data-testid="casino-plan-company-input"
                autoFocus
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Company / Casino name*"
                className="bg-bg border border-white/15 p-3 rounded-sm text-sm focus:border-gold/60 outline-none"
              />
              <button
                data-testid="casino-plan-checkout-btn"
                disabled={busy || !companyName.trim()}
                onClick={startCheckout}
                className="w-full font-display uppercase tracking-[0.2em] text-xs py-3.5 px-6 bg-gradient-to-r from-purple-500 to-gold text-bg rounded-sm hover:brightness-110 disabled:opacity-40 shadow-lg shadow-purple-500/30"
              >
                {busy ? "Redirecting to Stripe…" : `Subscribe · $${price.toLocaleString()}/mo`}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setCompanyName(""); setErr(""); }}
                className="text-[10px] uppercase tracking-[0.2em] text-muted hover:text-white font-display py-2"
              >
                Cancel
              </button>
              {err && <div data-testid="casino-plan-err" className="text-red-400 text-xs font-serif-accent">{err}</div>}
            </>
          )}
          {!user && (
            <div className="text-[11px] text-muted text-center font-serif-accent">Sign in first to subscribe.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Casino Owner tab (rendered inside BusinessPortal when user owns a casino sub)
function CasinoOwnerTab({ businesses, onOpenPortalBiz }) {
  const [sub, setSub] = useState(null);
  const [team, setTeam] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loadingSub, setLoadingSub] = useState(true);
  const [err, setErr] = useState("");

  // Create-team-member form
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newBizId, setNewBizId] = useState("");
  const [creating, setCreating] = useState(false);
  const [invite, setInvite] = useState(null); // {email, temp_password, business_id}
  const [createErr, setCreateErr] = useState("");

  const reload = async () => {
    setLoadingSub(true); setErr("");
    try {
      const [s, tm, an] = await Promise.all([
        axios.get(`${API}/casino/me`),
        axios.get(`${API}/casino/team-members`),
        axios.get(`${API}/casino/analytics/combined`),
      ]);
      setSub(s.data);
      setTeam(tm.data?.team_members || []);
      setAnalytics(an.data);
      if (!newBizId && (s.data?.business_ids?.length || 0) > 0) {
        setNewBizId(s.data.business_ids[0]);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load casino subscription.");
    } finally { setLoadingSub(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const createTeamMember = async (e) => {
    e?.preventDefault();
    setCreateErr(""); setInvite(null);
    if (!newEmail.includes("@")) { setCreateErr("Enter a valid email"); return; }
    if (!newBizId) { setCreateErr("Pick a business"); return; }
    setCreating(true);
    try {
      const r = await axios.post(`${API}/casino/team-members`, {
        casino_subscription_id: sub.id,
        business_id: newBizId,
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
      });
      setInvite({ email: newEmail.trim().toLowerCase(), temp_password: r.data.temp_password, business_id: newBizId });
      setNewEmail(""); setNewName("");
      await reload();
    } catch (e2) {
      setCreateErr(e2.response?.data?.detail || "Could not create team member");
    } finally { setCreating(false); }
  };

  const setActive = async (tm, active) => {
    try { await axios.patch(`${API}/casino/team-members/${tm.id}`, { active }); await reload(); } catch {}
  };
  const resetPw = async (tm) => {
    if (!window.confirm(`Generate a new temporary password for ${tm.email}?`)) return;
    try {
      const temp = Math.random().toString(36).slice(2, 12) + "!";
      const r = await axios.patch(`${API}/casino/team-members/${tm.id}`, { new_temp_password: temp });
      setInvite({ email: tm.email, temp_password: r.data.temp_password || temp, business_id: tm.business_id });
      await reload();
    } catch {}
  };
  const del = async (tm) => {
    if (!window.confirm(`Delete ${tm.email}? They will lose access immediately.`)) return;
    try { await axios.delete(`${API}/casino/team-members/${tm.id}`); await reload(); } catch {}
  };

  const copy = (s) => { try { navigator.clipboard.writeText(s); } catch {} };
  const inviteUrl = invite ? `${window.location.origin}/?team_login=${encodeURIComponent(invite.email)}` : "";

  if (loadingSub) return <div className="text-muted text-sm">Loading casino subscription…</div>;
  if (err) return <div data-testid="casino-tab-err" className="text-red-400 text-sm">{err}</div>;
  if (!sub) return null;

  const bizNames = {};
  (sub.businesses || []).forEach(b => { bizNames[b.id] = b.name; });
  const attached = sub.businesses || [];
  const usage = `${(sub.business_ids || []).length} of ${sub.business_limit || 5} businesses`;

  return (
    <div data-testid="casino-owner-tab" className="space-y-7">
      {/* Subscription info card */}
      <div className="border border-gold/30 rounded-sm p-5 bg-gradient-to-br from-gold/5 to-transparent">
        <div className="font-display uppercase text-[11px] tracking-[0.2em] text-gold">Casino Subscription</div>
        <div className="text-2xl font-display mt-1">{sub.company_name || "—"}</div>
        <div className="text-muted text-xs mt-1">
          Status: <span className="text-white">{sub.status}</span> · {usage} used
        </div>
        {sub.status === "past_due" && (
          <div className="mt-3 p-3 border border-red-500/40 bg-red-500/10 rounded-sm text-red-300 text-xs">
            ⚠ Subscription is past due. Please update payment or team access may be limited.
          </div>
        )}
      </div>

      {/* Attached businesses */}
      <div>
        <div className="font-display uppercase text-[11px] tracking-[0.2em] text-muted mb-2">Attached businesses</div>
        {attached.length === 0 && <div className="text-muted text-sm">No businesses attached yet. Ask an admin to attach up to {sub.business_limit || 5} businesses.</div>}
        <div className="grid gap-2">
          {attached.map(b => (
            <div key={b.id} data-testid={`casino-attached-biz-${b.id}`} className="flex items-center justify-between p-3 border border-white/10 rounded-sm bg-bg">
              <div>
                <div className="text-sm">{b.name}</div>
                <div className="text-muted text-[11px]">{b.category || ""} · {b.subscription_status || "—"}</div>
              </div>
              {onOpenPortalBiz && (
                <button data-testid={`casino-open-biz-${b.id}`} onClick={() => onOpenPortalBiz(b.id)} className="px-3 py-1.5 border border-gold/40 text-gold hover:bg-gold hover:text-bg font-display uppercase text-[10px] tracking-[0.2em] rounded-sm">Open</button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Team members */}
      <div>
        <div className="font-display uppercase text-[11px] tracking-[0.2em] text-muted mb-2">Team members</div>
        <form onSubmit={createTeamMember} className="grid sm:grid-cols-4 gap-2 mb-3 p-3 border border-white/10 rounded-sm bg-bg">
          <input data-testid="casino-new-tm-email" placeholder="team@casino.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="bg-surface border border-white/10 p-2 rounded-sm text-sm focus:border-gold/50" />
          <input data-testid="casino-new-tm-name" placeholder="Full name" value={newName} onChange={(e) => setNewName(e.target.value)} className="bg-surface border border-white/10 p-2 rounded-sm text-sm focus:border-gold/50" />
          <select data-testid="casino-new-tm-biz" value={newBizId} onChange={(e) => setNewBizId(e.target.value)} className="bg-surface border border-white/10 p-2 rounded-sm text-sm focus:border-gold/50">
            <option value="">— Business —</option>
            {attached.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button data-testid="casino-new-tm-submit" type="submit" disabled={creating || attached.length === 0} className="font-display uppercase text-[10px] tracking-[0.2em] py-2 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-40">
            {creating ? "Adding…" : "+ Invite"}
          </button>
        </form>
        {createErr && <div data-testid="casino-new-tm-err" className="text-red-400 text-xs mb-3">{createErr}</div>}

        {invite && (
          <div data-testid="casino-invite-card" className="p-3 border border-gold/50 bg-gold/10 rounded-sm mb-3 text-sm space-y-2">
            <div className="font-display uppercase text-[11px] tracking-[0.2em] text-gold">Invite ready — send this to your team member</div>
            <div>
              <div className="text-[11px] text-muted uppercase tracking-widest">Email</div>
              <div className="flex items-center gap-2"><span data-testid="casino-invite-email">{invite.email}</span><button onClick={() => copy(invite.email)} className="text-xs text-gold/80 hover:text-gold underline">Copy</button></div>
            </div>
            <div>
              <div className="text-[11px] text-muted uppercase tracking-widest">Temp password</div>
              <div className="flex items-center gap-2"><code data-testid="casino-invite-temp-pw" className="bg-bg px-2 py-1 rounded-sm">{invite.temp_password}</code><button onClick={() => copy(invite.temp_password)} className="text-xs text-gold/80 hover:text-gold underline">Copy</button></div>
            </div>
            <div>
              <div className="text-[11px] text-muted uppercase tracking-widest">Login link</div>
              <div className="flex items-center gap-2"><code className="bg-bg px-2 py-1 rounded-sm truncate max-w-[300px] block">{inviteUrl}</code><button onClick={() => copy(inviteUrl)} className="text-xs text-gold/80 hover:text-gold underline">Copy</button></div>
              <div className="text-[11px] text-muted mt-1">Tell them to sign in at Zip City → For Businesses → Team Member? Log in here</div>
            </div>
            <button onClick={() => setInvite(null)} className="text-xs text-muted hover:text-white">Dismiss</button>
          </div>
        )}

        <div className="grid gap-2">
          {team.length === 0 && <div className="text-muted text-sm">No team members yet — invite one above.</div>}
          {team.map(tm => (
            <div key={tm.id} data-testid={`casino-tm-row-${tm.id}`} className="flex items-center justify-between p-3 border border-white/10 rounded-sm bg-bg">
              <div>
                <div className="text-sm">{tm.name || tm.email} <span className="text-muted text-[11px]">· {tm.email}</span></div>
                <div className="text-muted text-[11px]">Business: {bizNames[tm.business_id] || tm.business_id} {tm.must_reset_password ? "· ⏱ pending first-login setup" : ""} {tm.active === false ? "· 🚫 inactive" : ""}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`casino-tm-reset-${tm.id}`} onClick={() => resetPw(tm)} className="text-[10px] font-display uppercase tracking-[0.2em] px-2 py-1 border border-white/20 hover:border-gold/40 rounded-sm">Reset PW</button>
                <button data-testid={`casino-tm-toggle-${tm.id}`} onClick={() => setActive(tm, !(tm.active !== false))} className="text-[10px] font-display uppercase tracking-[0.2em] px-2 py-1 border border-white/20 hover:border-gold/40 rounded-sm">{tm.active === false ? "Enable" : "Disable"}</button>
                <button data-testid={`casino-tm-del-${tm.id}`} onClick={() => del(tm)} className="text-[10px] font-display uppercase tracking-[0.2em] px-2 py-1 border border-red-500/40 text-red-300 hover:bg-red-500/20 rounded-sm">Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Combined analytics */}
      {analytics && (
        <div data-testid="casino-analytics">
          <div className="font-display uppercase text-[11px] tracking-[0.2em] text-muted mb-2">Combined analytics</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <div className="p-3 border border-white/10 rounded-sm bg-bg"><div className="text-[10px] uppercase text-muted">Businesses</div><div className="text-2xl font-display">{analytics.count}</div></div>
            <div className="p-3 border border-white/10 rounded-sm bg-bg"><div className="text-[10px] uppercase text-muted">Total views</div><div data-testid="casino-analytics-views" className="text-2xl font-display">{analytics.totals?.views || 0}</div></div>
            <div className="p-3 border border-white/10 rounded-sm bg-bg"><div className="text-[10px] uppercase text-muted">Total clicks</div><div data-testid="casino-analytics-clicks" className="text-2xl font-display">{analytics.totals?.clicks || 0}</div></div>
          </div>
          <div className="grid gap-1">
            {(analytics.per_business || []).map(pb => (
              <div key={pb.id} className="flex items-center justify-between p-2 border-b border-white/5 text-sm">
                <div>{pb.name}</div>
                <div className="text-muted text-xs">👁 {pb.views} · 🖱 {pb.clicks} · ✉ {pb.leads}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BusinessPortal({ open, onClose, businesses, onBusinessesChange, onRefreshAuth }) {
  useCategoriesLive();
  const { user } = useAuth();
  const userId = user?.user_id || ""; // body field is harmless; backend uses cookie
  const [myBizs, setMyBizs] = useState([]);
  const [tab, setTab] = useState("listings");
  const [loading, setLoading] = useState("");
  const [analytics, setAnalytics] = useState([]);

  const [selBizId, setSelBizId] = useState("");
  const selBiz = myBizs.find(b => b.id === selBizId) || null;
  const subActive = selBiz && ["active", "trialing"].includes(selBiz.subscription_status);

  const [claimName, setClaimName] = useState(""); const [claimLicense, setClaimLicense] = useState(""); const [claimMsg, setClaimMsg] = useState("");
  const [nbName, setNbName] = useState(""); const [nbCat, setNbCat] = useState("bars"); const [nbSub, setNbSub] = useState(""); const [nbDesc, setNbDesc] = useState(""); const [nbAddr, setNbAddr] = useState(""); const [nbPhone, setNbPhone] = useState(""); const [nbWebsite, setNbWebsite] = useState(""); const [nbImg, setNbImg] = useState(""); const [nbOffer, setNbOffer] = useState("NEW LISTING"); const [nbLicense, setNbLicense] = useState(""); const [nbMsg, setNbMsg] = useState("");
  const [extras, setExtras] = useState([]); const [catsMsg, setCatsMsg] = useState("");
  const [adCat, setAdCat] = useState(""); const [adHead, setAdHead] = useState(""); const [adBody, setAdBody] = useState(""); const [adImg, setAdImg] = useState(""); const [adCtaText, setAdCtaText] = useState("Visit"); const [adCtaUrl, setAdCtaUrl] = useState(""); const [adPhone, setAdPhone] = useState(""); const [adMsg, setAdMsg] = useState("");
  const [adLeadEnabled, setAdLeadEnabled] = useState(false);
  const [adLeadOffer, setAdLeadOffer] = useState("");
  const [leads, setLeads] = useState([]);
  const [showLeads, setShowLeads] = useState(false);
  const [popupCat, setPopupCat] = useState("");
  const [bannerSlot, setBannerSlot] = useState("new_deals");
  const [bannerSlotsInfo, setBannerSlotsInfo] = useState({});
  const [myBanners, setMyBanners] = useState([]);
  // Carousel
  const [carouselSlots, setCarouselSlots] = useState([]);
  const [carouselSlot, setCarouselSlot] = useState("slot_1");
  const [carouselInfo, setCarouselInfo] = useState({ price: 49, duration_days: 14 });
  const [myCarousels, setMyCarousels] = useState([]);
  const [carHead, setCarHead] = useState("");
  const [carBody, setCarBody] = useState("");
  const [carImg, setCarImg] = useState("");
  const [carCtaText, setCarCtaText] = useState("Visit");
  const [carCtaUrl, setCarCtaUrl] = useState("");
  const [carMsg, setCarMsg] = useState("");
  const [slotAvailability, setSlotAvailability] = useState(null);
  const [bHead, setBHead] = useState(""); const [bBody, setBBody] = useState(""); const [bImg, setBImg] = useState(""); const [bCtaText, setBCtaText] = useState("Visit"); const [bCtaUrl, setBCtaUrl] = useState(""); const [bMsg, setBMsg] = useState("");
  // Live pricing — refetched whenever portal opens to avoid stale cache (Stripe-mismatch fix)
  const [livePrices, setLivePrices] = useState(null);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/plans?t=${Date.now()}`).then(r => setLivePrices(r.data)).catch(() => {});
  }, [open]);
  const popupStdAmount = livePrices?.popup_ads?.popup_ad?.amount ?? 249;
  const popupProAmount = livePrices?.popup_ads?.popup_ad_pro?.amount ?? 124;
  const popupStdRegular = livePrices?.popup_ads?.popup_ad?.regular ?? 499;
  const bannerAmount = livePrices?.banner_ad ?? 99;
  const carouselAmount = livePrices?.carousel_ad ?? 49;

  // Extra category add-on (+$9.99 / month, recurring)
  const [extraCatOpen, setExtraCatOpen] = useState(false);
  const [extraCatChoice, setExtraCatChoice] = useState("");
  const [extraCatBusy, setExtraCatBusy] = useState(false);
  const [extraCatErr, setExtraCatErr] = useState("");
  useEffect(() => {
    // Auto-open after a fresh subscription purchase completes
    if (open && sessionStorage.getItem("zc_show_extra_cat_upsell") === "1") {
      const t = setTimeout(() => setExtraCatOpen(true), 1200);
      sessionStorage.removeItem("zc_show_extra_cat_upsell");
      return () => clearTimeout(t);
    }
  }, [open]);
  const buyExtraCategory = async () => {
    if (!extraCatChoice || !selBizId) { setExtraCatErr("Pick a category."); return; }
    setExtraCatBusy(true); setExtraCatErr("");
    try {
      const r = await axios.post(`${API}/stripe/extra-category`, {
        business_id: selBizId,
        category: extraCatChoice,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) { setExtraCatErr(e.response?.data?.detail || "Could not start checkout"); }
    finally { setExtraCatBusy(false); }
  };

  // 24/7 live-edit state
  const [editOffer, setEditOffer] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editImg, setEditImg] = useState("");
  const [editAddr, setEditAddr] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editWebsite, setEditWebsite] = useState("");
  const [editInstagram, setEditInstagram] = useState("");
  const [editTiktok, setEditTiktok] = useState("");
  const [editFacebook, setEditFacebook] = useState("");
  const [editX, setEditX] = useState("");
  const [editSub, setEditSub] = useState("");
  const [editMsg, setEditMsg] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [bizStats, setBizStats] = useState(null);

  useEffect(() => {
    if (!selBiz || !adCat) return;
    axios.get(`${API}/ads/${selBiz.id}?category=${adCat}`).then(r => {
      setAdHead(r.data.headline || ""); setAdBody(r.data.body || "");
      setAdImg(r.data.image_url || ""); setAdCtaText(r.data.cta_text || "Visit"); setAdCtaUrl(r.data.cta_url || "");
      setAdPhone(r.data.phone || "");
      setAdLeadEnabled(!!r.data.lead_capture_enabled);
      setAdLeadOffer(r.data.lead_offer_text || "");
    }).catch(() => {
      // No ad yet for THIS specific category — reset to a blank slate so the
      // owner can craft a different special per category (each (biz,cat) pair
      // is independent server-side).
      setAdHead(""); setAdBody(""); setAdImg("");
      setAdCtaText("Visit"); setAdCtaUrl("");
      setAdPhone(""); setAdLeadEnabled(false); setAdLeadOffer("");
    });
  }, [selBiz, adCat]);

  const reloadMyBizs = async () => {
    const r = await axios.get(`${API}/my-businesses`);
    setMyBizs(r.data);
    if (!selBizId && r.data[0]) setSelBizId(r.data[0].id);
  };

  useEffect(() => {
    if (!open) return;
    reloadMyBizs();
    axios.get(`${API}/banner-ads/slots`).then(r => setBannerSlotsInfo(r.data)).catch(() => {});
    axios.get(`${API}/carousel-ads/slots`).then(r => setCarouselInfo(r.data)).catch(() => {});
    axios.get(`${API}/carousel-ads/active`).then(r => setCarouselSlots(r.data || [])).catch(() => {});
  }, [open, userId]);

  useEffect(() => {
    if (selBiz) {
      setExtras(selBiz.extra_categories || []);
      setAdCat(selBiz.category);
      setPopupCat(selBiz.category);
      setEditOffer(selBiz.offer || "");
      setEditDesc(selBiz.desc || "");
      setEditImg(selBiz.img || "");
      setEditAddr(selBiz.address || "");
      setEditPhone(selBiz.phone || "");
      setEditWebsite(selBiz.website || "");
      setEditInstagram(selBiz.instagram || "");
      setEditTiktok(selBiz.tiktok || "");
      setEditFacebook(selBiz.facebook || "");
      setEditX(selBiz.x || "");
      setEditSub(selBiz.subcategory || "");
      setEditMsg("");
      axios.get(`${API}/businesses/${selBiz.id}/stats`).then(r => setBizStats(r.data)).catch(() => setBizStats(null));
      axios.get(`${API}/ads/analytics/${selBiz.id}`).then(r => setAnalytics(r.data)).catch(() => setAnalytics([]));
      axios.get(`${API}/banner-ads/my/${selBiz.id}`).then(r => setMyBanners(r.data)).catch(() => setMyBanners([]));
      axios.get(`${API}/carousel-ads/my/${selBiz.id}`).then(r => setMyCarousels(r.data)).catch(() => setMyCarousels([]));
    }
  }, [selBizId]);

  // When carouselSlot changes, prefill content from owner's active ad if any
  useEffect(() => {
    const mine = myCarousels.filter(c => c.slot === carouselSlot && c.active);
    const current = mine.find(c => {
      const now = new Date().toISOString();
      return (c.starts_at || "") <= now && (c.ends_at || "") >= now;
    }) || mine[0];
    if (current) {
      setCarHead(current.headline || ""); setCarBody(current.body || "");
      setCarImg(current.image_url || ""); setCarCtaText(current.cta_text || "Visit"); setCarCtaUrl(current.cta_url || "");
    } else {
      setCarHead(""); setCarBody(""); setCarImg(""); setCarCtaText("Visit"); setCarCtaUrl("");
    }
  }, [carouselSlot, myCarousels]);

  useEffect(() => {
    const mine = myBanners.filter(b => b.slot === bannerSlot && b.active);
    const current = mine.find(b => {
      const now = new Date().toISOString();
      return (b.starts_at || "") <= now && (b.ends_at || "") >= now;
    }) || mine[0];
    if (current) {
      setBHead(current.headline || ""); setBBody(current.body || ""); setBImg(current.image_url || "");
      setBCtaText(current.cta_text || "Visit"); setBCtaUrl(current.cta_url || "");
    } else {
      setBHead(""); setBBody(""); setBImg(""); setBCtaText("Visit"); setBCtaUrl("");
    }
    axios.get(`${API}/banner-ads/availability/${bannerSlot}`).then(r => setSlotAvailability(r.data)).catch(() => setSlotAvailability(null));
  }, [bannerSlot, myBanners]);

  const submitClaim = async () => {
    if (!claimName.trim()) return setClaimMsg("Enter a business name");
    if (!claimLicense.trim()) return setClaimMsg("Business license number required");
    try {
      const r = await axios.post(`${API}/business/claim`, { name: claimName, userId, license_number: claimLicense });
      setClaimMsg("✓ " + r.data.message);
      setClaimName(""); setClaimLicense("");
      await reloadMyBizs();
      onBusinessesChange?.();
    } catch (e) { setClaimMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const submitNew = async () => {
    if (!nbName || !nbCat) return setNbMsg("Name + category required");
    if (!nbLicense.trim() && false) return setNbMsg("Business license number required");
    try {
      const r = await axios.post(`${API}/business/submit`, { name: nbName, category: nbCat, subcategory: nbSub, desc: nbDesc, address: nbAddr, phone: nbPhone, website: nbWebsite, img: nbImg, offer: nbOffer, license_number: nbLicense, userId });
      setNbMsg("✓ Submitted — pick a plan and add your card to go live once approved");
      setNbName(""); setNbDesc(""); setNbAddr(""); setNbPhone(""); setNbWebsite(""); setNbImg(""); setNbSub(""); setNbLicense("");
      await reloadMyBizs();
      setSelBizId(r.data.business_id);
      onBusinessesChange?.();
      // Auto-advance to the Plans tab so the owner picks a subscription right away
      setTab("plans");
    } catch (e) { setNbMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const subscribe = async (planId) => {
    if (!selBizId) return alert("Select or create a business first");
    setLoading(planId);
    try {
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      const ref = urlRef || localStorage.getItem("zc_ref") || null;
      const res = await axios.post(`${API}/stripe/subscribe`, { plan: planId, origin_url: window.location.origin, userId, business_id: selBizId, ref });
      window.location = res.data.url;
    } catch (e) { alert("Checkout error: " + (e.response?.data?.detail || e.message)); setLoading(""); }
  };

  const buyPopupAd = async (pkg, category) => {
    if (!selBizId) return alert("Select a business first");
    if (!category) return alert("Pick a category");
    setLoading(pkg);
    try {
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      const ref = urlRef || localStorage.getItem("zc_ref") || null;
      const expected = pkg === "popup_ad_pro" ? popupProAmount : popupStdAmount;
      const res = await axios.post(`${API}/stripe/popup-ad`, { package_id: pkg, origin_url: window.location.origin, userId, business_id: selBizId, category, ref, expected_amount: expected });
      window.location = res.data.url;
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); setLoading(""); }
  };

  const buyBannerAd = async () => {
    if (!selBizId) return alert("Select a business first");
    setLoading("banner_ad");
    try {
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      const ref = urlRef || localStorage.getItem("zc_ref") || null;
      const res = await axios.post(`${API}/stripe/banner-ad`, { slot: bannerSlot, origin_url: window.location.origin, userId, business_id: selBizId, ref, expected_amount: bannerAmount });
      window.location = res.data.url;
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); setLoading(""); }
  };

  const saveBannerContent = async () => {
    if (!selBizId) return;
    if (!bHead || !bBody) return setBMsg("Fill headline and body");
    try {
      await axios.post(`${API}/banner-ads/content`, {
        business_id: selBizId, userId, slot: bannerSlot,
        headline: bHead, body: bBody, image_url: bImg, cta_text: bCtaText, cta_url: bCtaUrl,
      });
      setBMsg(`✓ Banner content saved for ${bannerSlot.replace("_", " ")}`);
      axios.get(`${API}/banner-ads/my/${selBizId}`).then(r => setMyBanners(r.data)).catch(() => {});
    } catch (e) { setBMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const buyCarouselAd = async () => {
    if (!selBizId) return alert("Select a business first");
    setLoading("carousel_ad");
    try {
      const urlRef = new URLSearchParams(window.location.search).get("ref");
      const ref = urlRef || localStorage.getItem("zc_ref") || null;
      const res = await axios.post(`${API}/stripe/carousel-ad`, { slot: carouselSlot, origin_url: window.location.origin, business_id: selBizId, ref, expected_amount: carouselAmount });
      window.location = res.data.url;
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); setLoading(""); }
  };

  const saveCarouselContent = async () => {
    if (!selBizId) return;
    if (!carHead || !carImg) return setCarMsg("Headline and image required");
    try {
      await axios.post(`${API}/carousel-ads/content`, {
        business_id: selBizId, slot: carouselSlot,
        headline: carHead, body: carBody, image_url: carImg, cta_text: carCtaText, cta_url: carCtaUrl,
      });
      setCarMsg(`✓ Carousel content saved for ${carouselSlot.replace("_", " ")}`);
      axios.get(`${API}/carousel-ads/my/${selBizId}`).then(r => setMyCarousels(r.data)).catch(() => {});
    } catch (e) { setCarMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const manageBilling = async () => {
    try {
      const res = await axios.post(`${API}/stripe/billing-portal`, { userId, business_id: selBizId, origin_url: window.location.origin });
      window.location = res.data.url;
    } catch (e) { alert("Billing portal: " + (e.response?.data?.detail || e.message)); }
  };

  const saveListingEdits = async () => {
    if (!selBizId) return;
    setEditBusy(true); setEditMsg("");
    try {
      await axios.put(`${API}/businesses/${selBizId}`, {
        offer: editOffer,
        desc: editDesc,
        img: editImg,
        address: editAddr,
        phone: editPhone,
        website: editWebsite,
        instagram: editInstagram,
        tiktok: editTiktok,
        facebook: editFacebook,
        subcategory: editSub,
      });
      setEditMsg("✓ Saved — your listing is updated live.");
      await reloadMyBizs();
      onBusinessesChange?.();
    } catch (e) {
      setEditMsg("Error: " + (e.response?.data?.detail || e.message));
    }
    setEditBusy(false);
  };

  const saveCategories = async () => {
    try {
      await axios.post(`${API}/business/categories`, { business_id: selBizId, userId, extra_categories: extras });
      setCatsMsg("✓ Categories updated");
      await reloadMyBizs();
      onBusinessesChange?.();
    } catch (e) { setCatsMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const publishAd = async () => {
    if (!selBizId || !adHead || !adBody || !adCat) return setAdMsg("Fill all fields");
    try {
      await axios.post(`${API}/ads`, { business_id: selBizId, userId, category: adCat, headline: adHead, body: adBody, image_url: adImg, cta_text: adCtaText, cta_url: adCtaUrl, phone: adPhone, lead_capture_enabled: adLeadEnabled, lead_offer_text: adLeadOffer, active: true });
      setAdMsg(`✓ Published for ${CAT_TITLE[adCat]}${adLeadEnabled ? " — lead capture ON" : ""}`);
      axios.get(`${API}/ads/analytics/${selBizId}`).then(r => setAnalytics(r.data)).catch(() => {});
    } catch (e) { setAdMsg("Error: " + (e.response?.data?.detail || e.message)); }
  };

  const loadLeads = async () => {
    if (!selBizId) return;
    try {
      const r = await axios.get(`${API}/ads/leads/${selBizId}`);
      setLeads(r.data);
      setShowLeads(true);
    } catch (e) { setAdMsg("Error loading leads: " + (e.response?.data?.detail || e.message)); }
  };

  const downloadLeadsCsv = () => {
    if (!leads.length) return;
    const rows = [["Email", "Name", "Category", "Code", "Captured"]];
    leads.forEach(l => rows.push([l.email || "", l.name || "", l.category, l.redemption_code, new Date(l.captured_at).toISOString()]));
    const csv = rows.map(r => r.map(c => `"${(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `leads-${selBizId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: "listings", label: "My Listings" },
    ...(user?.is_team_member ? [] : [{ id: "plans", label: "Plans" }]),
    { id: "ad", label: "24/7 Ad" },
    { id: "popup", label: "Pop-up Ad" },
    { id: "banner", label: "Banner Ad" },
    { id: "carousel", label: "Carousel" },
    ...(user?.is_team_member ? [] : [{ id: "emails", label: "Emails" }]),
    ...(user?.casino_subscription_id && !user?.is_team_member ? [{ id: "casino", label: "Casino" }] : []),
  ];

  const myBizCats = selBiz ? [selBiz.category, ...(selBiz.extra_categories || [])] : [];

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/92 backdrop-blur-2xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div data-testid="business-portal" onClick={(e) => e.stopPropagation()} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} className="w-full max-w-3xl bg-surface border border-white/10 rounded-sm overflow-hidden max-h-[92vh] overflow-y-auto">
            <div className="h-32 relative overflow-hidden">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1714865212999-a9817814b080?w=600')" }} />
              <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/60 to-surface/10" />
              <button data-testid="portal-close" onClick={onClose} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/70 hover:bg-bg flex items-center justify-center">✕</button>
              <div className="absolute bottom-4 left-6">
                <div className="font-serif-accent text-gold text-sm">For operators ~</div>
                <div className="font-display uppercase text-3xl tracking-tight">Business Portal</div>
              </div>
            </div>

            {myBizs.length > 0 && (
              <div className="px-6 md:px-8 pt-5 pb-2 border-b border-white/5 flex items-center gap-3">
                <div className="text-xs uppercase tracking-[0.2em] text-muted font-display whitespace-nowrap">Active:</div>
                <select data-testid="active-biz-select" value={selBizId} onChange={(e) => setSelBizId(e.target.value)} className="flex-1 bg-bg border border-white/10 p-2.5 rounded-sm text-sm focus:border-gold/50">
                  {myBizs.map(b => <option key={b.id} value={b.id}>{`${b.name} — ${b.subscription_status}`}{b.founding_member ? " · 👑 Founding Member" : ""}</option>)}
                </select>
                {subActive && selBiz?.stripe_customer_id && !user?.is_team_member && (
                  <>
                    <button data-testid="add-category-btn" onClick={() => setExtraCatOpen(true)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 bg-pink-500/15 border border-pink-500/40 text-pink-300 hover:bg-pink-500 hover:text-white rounded-sm transition-all">+ Category $9.99</button>
                    <button data-testid="billing-portal-btn" onClick={manageBilling} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-white/20 hover:border-gold/40 rounded-sm">Manage Billing</button>
                  </>
                )}
              </div>
            )}

            <div className="border-b border-white/5 flex overflow-x-auto no-scrollbar">
              {tabs.map((t) => (
                <button key={t.id} data-testid={`portal-tab-${t.id}`} onClick={() => setTab(t.id)} className={`px-5 py-3.5 font-display uppercase text-[11px] tracking-[0.2em] whitespace-nowrap transition-colors ${tab === t.id ? "text-gold border-b-2 border-gold" : "text-muted hover:text-white"}`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-6 md:p-8">
              {tab === "listings" && (
                <div className="space-y-6">
                  {selBiz && (
                    <div data-testid="edit-listing-card" className="border border-gold/30 rounded-sm p-5 bg-gradient-to-br from-gold/5 to-transparent">
                      {selBiz.status === "pending_approval" && (
                        <div data-testid="pending-approval-banner" className="mb-4 p-3 border border-gold/50 bg-gold/10 rounded-sm">
                          <div className="font-display uppercase text-[11px] tracking-[0.2em] text-gold">⏱ Pending admin approval</div>
                          <div className="text-muted text-xs mt-1 leading-relaxed">Your listing is submitted and waiting for admin review. You can keep editing details here — everything goes live the moment we approve it.</div>
                        </div>
                      )}
                      {!subActive && (
                        <div data-testid="pick-plan-cta" className="mb-4 p-4 border border-gold/60 bg-gradient-to-br from-gold/15 to-gold/5 rounded-sm">
                          <div className="font-serif-accent text-gold text-sm">~ Next step</div>
                          <div className="font-display uppercase text-base tracking-tight mt-0.5">Pick a plan & add your card</div>
                          <div className="text-muted text-xs mt-1 leading-relaxed">{livePrices?.founding_blurb || "Your listing needs an active subscription to stay live."}</div>
                          <button data-testid="go-to-plans-btn" onClick={() => setTab("plans")} className="mt-3 font-display uppercase tracking-[0.2em] text-[11px] py-2.5 px-5 bg-gold-grad text-bg rounded-sm hover:brightness-110">
                            See plans →
                          </button>
                        </div>
                      )}
                      {selBiz.status === "rejected" && (
                        <div className="mb-4 p-3 border border-red-500/50 bg-red-500/10 rounded-sm">
                          <div className="font-display uppercase text-[11px] tracking-[0.2em] text-red-400">✕ Listing rejected</div>
                          <div className="text-muted text-xs mt-1">Reach out to admin if you believe this was in error.</div>
                        </div>
                      )}
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-serif-accent text-gold text-sm">~ Live edits</div>
                          <div className="font-display uppercase text-lg tracking-tight mt-0.5">Edit {selBiz.name}</div>
                          <div className="text-muted text-xs mt-1">Change your image and special anytime — updates go live instantly.</div>
                        </div>
                      </div>

                      {bizStats && (
                        <div data-testid="biz-stats-panel" className="mt-4 grid grid-cols-4 gap-2">
                          <div className="border border-white/10 rounded-sm p-3 text-center">
                            <div className="font-display text-2xl gold-text">{bizStats.views_total || 0}</div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-muted mt-1">Views</div>
                          </div>
                          <div className="border border-white/10 rounded-sm p-3 text-center">
                            <div className="font-display text-2xl gold-text">{bizStats.views_today || 0}</div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-muted mt-1">Today</div>
                          </div>
                          <div className="border border-white/10 rounded-sm p-3 text-center">
                            <div className="font-display text-2xl gold-text">{bizStats.ad_clicks || 0}</div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-muted mt-1">Ad clicks</div>
                          </div>
                          <div className="border border-white/10 rounded-sm p-3 text-center">
                            <div className="font-display text-2xl gold-text">{bizStats.leads_captured || 0}</div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-muted mt-1">Leads</div>
                          </div>
                        </div>
                      )}

                      <div className="mt-4 space-y-3">
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.2em] text-muted font-display mb-1.5">Your special / offer (shows on card)</label>
                          <input data-testid="edit-offer" value={editOffer} onChange={(e) => setEditOffer(e.target.value)} placeholder="e.g. HAPPY HOUR · 2-for-1 · 4-7pm" className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.2em] text-muted font-display mb-1.5">Featured image</label>
                          <ImageUploader value={editImg} onChange={setEditImg} />
                          <div className="text-[10px] text-muted mt-1">Tip: upload a landscape photo — we auto-crop to 4:3 and show you a preview first.</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <input data-testid="edit-phone" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="Phone" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                          <SubcategoryPicker category={selBiz?.category} value={editSub} onChange={setEditSub} testId="edit-sub" />
                        </div>
                        <input data-testid="edit-website" value={editWebsite} onChange={(e) => setEditWebsite(e.target.value)} placeholder="Website (optional) · https://…" className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <input data-testid="edit-instagram" value={editInstagram} onChange={(e) => setEditInstagram(e.target.value)} placeholder="Instagram (URL or @handle)" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                          <input data-testid="edit-tiktok" value={editTiktok} onChange={(e) => setEditTiktok(e.target.value)} placeholder="TikTok (URL or @handle)" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                          <input data-testid="edit-facebook" value={editFacebook} onChange={(e) => setEditFacebook(e.target.value)} placeholder="Facebook (URL or page name)" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                          <input data-testid="edit-x" value={editX} onChange={(e) => setEditX(e.target.value)} placeholder="X / Twitter (URL or @handle)" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                        </div>
                        <input data-testid="edit-addr" value={editAddr} onChange={(e) => setEditAddr(e.target.value)} placeholder="Address" className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                        <textarea data-testid="edit-desc" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} placeholder="Short description" className="w-full bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                        <button data-testid="edit-save-btn" onClick={saveListingEdits} disabled={editBusy} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">
                          {editBusy ? "Saving…" : "Save changes live"}
                        </button>
                        {editMsg && <div data-testid="edit-msg" className={`text-sm font-serif-accent ${editMsg.startsWith("✓") ? "text-gold" : "text-red-400"}`}>{editMsg}</div>}
                      </div>
                    </div>
                  )}

                  {selBiz && (
                    <div className="border border-white/10 rounded-sm p-5 bg-bg/30">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display uppercase text-lg tracking-tight">{selBiz.name}</div>
                        {selBiz.founding_member && (
                          <span data-testid="founding-badge" className="font-display uppercase text-[10px] tracking-[0.18em] px-2 py-1 bg-gold-grad text-bg rounded-sm">👑 Founding Member · for life</span>
                        )}
                        {selBiz.locked_price_cents > 0 && (
                          <span data-testid="locked-price-badge" className="font-display uppercase text-[10px] tracking-[0.18em] px-2 py-1 border border-gold/40 text-gold rounded-sm">🔒 ${(selBiz.locked_price_cents / 100).toFixed(2)} /mo · locked</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                        <div className="text-muted uppercase tracking-widest text-[10px]">Plan<div className="text-white font-display text-lg normal-case tracking-tight">{selBiz.plan || "—"}</div></div>
                        <div className="text-muted uppercase tracking-widest text-[10px]">Status<div className={`font-display text-lg tracking-tight ${subActive ? "gold-text" : "text-white"}`}>{selBiz.subscription_status}</div></div>
                        <div className="text-muted uppercase tracking-widest text-[10px]">Categories allowed<div className="text-white font-display text-lg">{selBiz.categories_allowed || 0}</div></div>
                        <div className="text-muted uppercase tracking-widest text-[10px]">Listed in<div className="text-white font-display text-lg">{1 + (selBiz.extra_categories?.length || 0)}</div></div>
                      </div>
                      {subActive && (
                        <div className="mt-5">
                          <div className="text-xs uppercase tracking-[0.2em] text-muted font-display mb-2">Extra categories ({selBiz.extra_categories?.length || 0} / {(selBiz.categories_allowed || 1) - 1})</div>
                          <div className="flex flex-wrap gap-2">
                            {CATEGORIES.filter(c => c.key !== selBiz.category).map(c => {
                              const on = extras.includes(c.key);
                              const max = (selBiz.categories_allowed || 1) - 1;
                              const disabled = !on && extras.length >= max;
                              return (
                                <button key={c.key} data-testid={`extra-cat-${c.key}`} disabled={disabled}
                                  onClick={() => setExtras(on ? extras.filter(x => x !== c.key) : [...extras, c.key])}
                                  className={`text-[11px] font-display uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border ${on ? "bg-gold text-bg border-gold" : disabled ? "border-white/5 text-muted/40 cursor-not-allowed" : "border-white/10 text-muted hover:border-gold/40 hover:text-gold"}`}>
                                  {c.title}
                                </button>
                              );
                            })}
                          </div>
                          <button data-testid="save-extras" onClick={saveCategories} className="mt-3 font-display uppercase tracking-[0.2em] text-xs py-2.5 px-5 bg-gold text-bg rounded-sm hover:brightness-110">Save Categories</button>
                          {catsMsg && <div className="text-sm text-gold mt-2 font-serif-accent">{catsMsg}</div>}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="border border-white/10 rounded-sm p-5 bg-bg/30">
                      <div className="font-display uppercase tracking-tight text-lg">Claim Existing</div>
                      <p className="text-muted text-sm mt-1 mb-3">If your business is already listed.</p>
                      <input data-testid="claim-input claim-name-input" value={claimName} onChange={(e) => setClaimName(e.target.value)} placeholder="Business name (exact)…" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm mb-2" />
                      <input data-testid="claim-license" value={claimLicense} onChange={(e) => setClaimLicense(e.target.value)} placeholder="Business license # (optional)" className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm mb-3" />
                      <button data-testid="claim-submit claim-submit-btn" onClick={submitClaim} className="w-full font-display uppercase tracking-[0.2em] text-xs py-3 border border-white/20 hover:bg-white hover:text-bg rounded-sm">Claim</button>
                      {claimMsg && <div className="text-sm text-gold mt-2 font-serif-accent">{claimMsg}</div>}
                    </div>
                    <div className="border border-gold/30 rounded-sm p-5 bg-gradient-to-br from-gold/5 to-transparent">
                      <div className="font-display uppercase tracking-tight text-lg">Add New Listing</div>
                      <p className="text-muted text-sm mt-1 mb-3">Not yet on Zip City?</p>
                      <div className="grid grid-cols-2 gap-2">
                        <input data-testid="new-name" value={nbName} onChange={(e) => setNbName(e.target.value)} placeholder="Business name*" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm col-span-2" />
                        <input data-testid="new-license" value={nbLicense} onChange={(e) => setNbLicense(e.target.value)} placeholder="Business license # (optional)" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm col-span-2" />
                        <select data-testid="new-cat" value={nbCat} onChange={(e) => setNbCat(e.target.value)} className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm">
                          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.title}</option>)}
                        </select>
                        <SubcategoryPicker category={nbCat} value={nbSub} onChange={setNbSub} testId="new-sub" />
                        <input data-testid="new-addr" value={nbAddr} onChange={(e) => setNbAddr(e.target.value)} placeholder="Address" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm col-span-2" />
                        <input data-testid="new-phone" value={nbPhone} onChange={(e) => setNbPhone(e.target.value)} placeholder="Phone (e.g. 702-555-0199)" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                        <input data-testid="new-website" value={nbWebsite} onChange={(e) => setNbWebsite(e.target.value)} placeholder="Website (https://…)" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm" />
                        <div className="col-span-2"><ImageUploader value={nbImg} onChange={setNbImg} /></div>
                        <input data-testid="new-offer" value={nbOffer} onChange={(e) => setNbOffer(e.target.value)} placeholder="Offer" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm col-span-2" />
                        <textarea data-testid="new-desc" value={nbDesc} onChange={(e) => setNbDesc(e.target.value)} rows={2} placeholder="Short description" className="bg-bg border border-white/10 p-2.5 rounded-sm text-sm col-span-2" />
                      </div>
                      <button data-testid="new-submit" onClick={submitNew} className="w-full mt-3 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Create Listing</button>
                      {nbMsg && <div className="text-sm text-gold mt-2 font-serif-accent">{nbMsg}</div>}
                    </div>
                  </div>
                </div>
              )}

              {tab === "plans" && (
                <div className="space-y-5">
                  {(() => {
                    const fs = livePrices?.founding_state;
                    const isFoundingOn = !!livePrices?.founding_member;
                    return (
                      <>
                        {isFoundingOn && (
                          <div data-testid="founding-card" className="border-2 border-gold/60 rounded-sm p-5 bg-gradient-to-br from-gold/20 via-gold/8 to-transparent shadow-xl shadow-gold/10">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                              <div className="flex-1 min-w-[220px]">
                                <div className="inline-block font-display uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 rounded-sm bg-gold-grad text-bg">👑 Founding Member · Limited</div>
                                <div className="font-display uppercase text-2xl tracking-tight mt-3">{livePrices?.founding_blurb || "Founding-Member 50% off — only the first 100 subscribers. Your price locks for life."}</div>
                                {fs && (
                                  <div className="mt-3">
                                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.22em] text-gold font-display mb-1">
                                      <span>{fs.count} of {fs.cap} subscriptions claimed</span>
                                      <span>{Math.max(fs.cap - fs.count, 0)} left</span>
                                    </div>
                                    <div className="h-2 bg-bg/60 rounded-full overflow-hidden border border-gold/30">
                                      <div className="h-full bg-gold-grad" style={{ width: `${Math.min(100, (fs.count / Math.max(fs.cap, 1)) * 100)}%` }} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {!isFoundingOn && fs && (
                          <div data-testid="founding-closed" className="border border-white/10 rounded-sm p-4 bg-bg/40">
                            <div className="font-serif-accent text-muted text-sm">~ Founding Member pricing</div>
                            <div className="font-display uppercase text-base tracking-tight mt-0.5">All 100 Founding Member slots have been claimed</div>
                            <div className="text-muted text-xs mt-1">Regular plan pricing applies for new subscriptions below.</div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  {!selBizId && <div className="border border-amber-400/30 bg-amber-400/5 p-4 rounded-sm font-serif-accent text-amber-200">Create or claim a listing first to subscribe.</div>}
                  {(() => {
                    const ref = new URLSearchParams(window.location.search).get("ref") || localStorage.getItem("zc_ref");
                    if (ref) return (
                      <div data-testid="plans-promo-note" className="border border-gold/40 rounded-sm p-4 bg-gradient-to-br from-gold/10 to-transparent font-display uppercase text-xs tracking-[0.18em] text-gold">
                        🎟 Referral <span className="font-serif-accent normal-case tracking-normal text-white"> · code {ref} · </span> 30-day free trial applied at checkout
                      </div>
                    );
                    return null;
                  })()}
                  <div className="grid md:grid-cols-3 gap-4">
                    {PRICING.map((p) => {
                      // Use live prices from /api/plans (admin-editable) when available
                      const live = livePrices?.subscriptions?.[p.id];
                      const livePrice = live?.price ?? p.price;
                      const baseShown = live?.base_price ?? livePrice;
                      const liveRegular = live?.regular_price ?? p.regular;
                      const liveDesc = live?.description;
                      const isFounding = !!livePrices?.founding_member;
                      return (
                        <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-sm p-5 border transition-all ${p.featured ? "border-gold/50 bg-gradient-to-br from-gold/8 to-transparent" : "border-white/10 bg-bg/40"}`}>
                          {p.featured && <div className="font-serif-accent text-gold text-xs mb-1">~ Most popular</div>}
                          <div className="font-display uppercase text-xl tracking-tight">{p.label}</div>
                          <div className="mt-2 flex items-baseline gap-2">
                            <span className="font-display text-4xl gold-text">${livePrice}</span>
                            {isFounding && liveRegular > livePrice && <span className="text-muted text-base line-through opacity-60">${liveRegular}</span>}
                            <span className="text-muted text-xs uppercase tracking-widest">/mo</span>
                          </div>
                          {isFounding && <div className="font-serif-accent text-gold text-[11px] mt-0.5">👑 Founding Member · locked for life</div>}
                          <div className="text-muted text-xs mt-1.5 leading-relaxed">{liveDesc || p.tagline}</div>
                          <ul className="text-sm text-muted space-y-1.5 my-4">{p.features.map((f, i) => <li key={i} className="flex gap-2"><span className="text-gold">✦</span> {f}</li>)}</ul>
                          <button data-testid={`subscribe-${p.id}`} disabled={loading === p.id || !selBizId} onClick={() => subscribe(p.id)} className={`w-full font-display uppercase tracking-[0.2em] text-[11px] py-3 rounded-sm transition-all disabled:opacity-50 ${p.featured ? "bg-gold-grad text-bg hover:brightness-110" : "border border-white/20 hover:bg-white hover:text-bg"}`}>
                            {loading === p.id ? "Redirecting…" : `Lock in $${livePrice}/mo`}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-muted text-xs font-serif-accent">All plans auto-renew monthly. 14-day free trial. Cancel anytime via Manage Billing.</p>

                  {/* Casino Business enterprise tier — self-serve */}
                  <CasinoPlanCard livePrices={livePrices} />

                  {/* Always-visible add-on so owners see they can pay extra for more category exposure */}
                  <div className="mt-6 border-2 border-pink-500/40 rounded-sm p-5 bg-gradient-to-br from-pink-500/10 to-transparent ad-247-glow" data-testid="extra-cat-banner">
                    <div className="flex items-start gap-4 flex-wrap">
                      <div className="flex-1 min-w-[200px]">
                        <div className="inline-block font-display uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 rounded-sm bg-pink-500 text-white">Add-on</div>
                        <div className="font-display uppercase text-xl tracking-tight mt-2">Need More Categories?</div>
                        <p className="text-muted text-sm font-serif-accent mt-1">Add another category to your listing for just <span className="text-gold font-display">${(livePrices?.extra_category?.price ?? 9.99).toFixed(2)} / month</span>. Stack as many as you need.</p>
                      </div>
                      <button
                        data-testid="plan-add-category-btn"
                        disabled={!selBizId || !subActive}
                        onClick={() => setExtraCatOpen(true)}
                        className="font-display uppercase tracking-[0.2em] text-xs py-3 px-5 bg-pink-500 text-white rounded-sm hover:brightness-110 disabled:opacity-40 shadow-lg shadow-pink-500/30"
                      >
                        + Add Category ${(livePrices?.extra_category?.price ?? 9.99).toFixed(2)}/mo
                      </button>
                    </div>
                    {!subActive && <div className="text-[11px] text-amber-300 font-serif-accent mt-3">Subscribe to any plan first — then you can stack categories.</div>}
                  </div>
                </div>
              )}

              {tab === "ad" && (
                <div>
                  <div className="font-display uppercase text-2xl tracking-tight mb-1">24/7 Business Spotlight</div>
                  <p className="font-serif-accent text-gold text-sm mb-5">Change your landing content anytime — different ad for each category you list in.</p>
                  {!selBizId && <div className="border border-amber-400/30 bg-amber-400/5 p-4 rounded-sm font-serif-accent text-amber-200">Select a business first.</div>}
                  {selBizId && !subActive && <div className="border border-amber-400/30 bg-amber-400/5 p-4 rounded-sm font-serif-accent text-amber-200">Active subscription required — visit the Plans tab.</div>}
                  {selBizId && subActive && (
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="md:col-span-2 border border-gold/30 bg-gold/5 rounded-sm p-3 text-xs font-serif-accent text-gold/90">
                        📍 You're editing the ad for <span className="font-display uppercase tracking-wider text-gold">{CAT_TITLE[adCat] || adCat}</span> — every category you're listed in has its own independent ad. Switch the dropdown to edit a different one.
                      </div>
                      <select data-testid="ad-cat" value={adCat} onChange={(e) => setAdCat(e.target.value)} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2">
                        {myBizCats.map(c => <option key={c} value={c}>{CAT_TITLE[c] || c}</option>)}
                      </select>
                      <input data-testid="ad-headline" value={adHead} onChange={(e) => setAdHead(e.target.value)} placeholder="Headline" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                      <textarea data-testid="ad-body" value={adBody} onChange={(e) => setAdBody(e.target.value)} rows={3} placeholder="Body — describe the offer, fine print…" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                      <div className="md:col-span-2">
                        <ImageUploader value={adImg} onChange={setAdImg} />
                        <div className="flex items-center justify-between mt-1 gap-3 flex-wrap">
                          <div className="text-[10px] text-muted font-serif-accent">📐 Recommended: <span className="text-gold">1200×1200 (square, 1:1)</span> · max 4 MB</div>
                          <a href="https://www.canva.com/create/flyers/" target="_blank" rel="noreferrer" className="text-[10px] uppercase tracking-[0.2em] text-gold hover:underline font-display">🎨 Design in Canva ↗</a>
                        </div>
                      </div>
                      <input data-testid="ad-cta-text" value={adCtaText} onChange={(e) => setAdCtaText(e.target.value)} placeholder="CTA text (e.g. Visit, Book Now)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                      <input data-testid="ad-cta-url" value={adCtaUrl} onChange={(e) => setAdCtaUrl(e.target.value)} placeholder="CTA URL (optional)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                      <input data-testid="ad-phone" value={adPhone} onChange={(e) => setAdPhone(e.target.value)} placeholder="Phone (optional — adds CALL button)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />

                      <div className="md:col-span-2 border border-white/10 rounded-sm p-4 bg-bg/40">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input data-testid="ad-lead-toggle" type="checkbox" checked={adLeadEnabled} onChange={(e) => setAdLeadEnabled(e.target.checked)} className="mt-1 accent-yellow-500" />
                          <div className="flex-1">
                            <div className="font-display uppercase text-sm tracking-tight">Continue with Google · Lead capture</div>
                            <div className="text-muted text-xs font-serif-accent mt-0.5">When ON, your popup shows a "Continue with Google" button. Visitors who tap it save their email + get a redemption code only your staff can verify. You'll see them in your leads list.</div>
                          </div>
                        </label>
                        {adLeadEnabled && (
                          <input
                            data-testid="ad-lead-offer"
                            value={adLeadOffer}
                            onChange={(e) => setAdLeadOffer(e.target.value)}
                            placeholder='e.g. "Save 10% — Show this code at the door"'
                            className="w-full mt-3 bg-bg border border-gold/30 p-3 rounded-sm text-sm focus:border-gold/60"
                          />
                        )}
                      </div>

                      <div className="md:col-span-2 flex gap-2">
                        <button data-testid="ad-publish" onClick={publishAd} className="flex-1 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Publish Spotlight</button>
                        {adLeadEnabled && <button data-testid="ad-leads-view" onClick={loadLeads} type="button" className="font-display uppercase tracking-[0.2em] text-xs py-3 px-4 border border-gold/40 text-gold hover:bg-gold hover:text-bg rounded-sm">📥 Leads</button>}
                      </div>
                      {adMsg && <div className="text-sm text-gold md:col-span-2 font-serif-accent">{adMsg}</div>}
                      {analytics.length > 0 && (
                        <div data-testid="ad-analytics" className="md:col-span-2 mt-2">
                          <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display mb-2">Spotlight performance</div>
                          <div className="space-y-2">
                            {analytics.map((a, i) => (
                              <div key={i} className="flex items-center justify-between gap-3 bg-bg border border-white/5 p-3 rounded-sm">
                                <div className="min-w-0">
                                  <div className="font-display uppercase text-xs tracking-[0.15em] text-gold">{CAT_TITLE[a.category] || a.category}</div>
                                  <div className="text-sm truncate">{a.headline}</div>
                                </div>
                                <div className="flex gap-4 text-right shrink-0">
                                  <div><div className="font-display text-sm gold-text">{a.impressions}</div><div className="text-[9px] uppercase tracking-widest text-muted">Views</div></div>
                                  <div><div className="font-display text-sm gold-text">{a.clicks}</div><div className="text-[9px] uppercase tracking-widest text-muted">Clicks</div></div>
                                  <div><div className="font-display text-sm gold-text">{a.ctr}%</div><div className="text-[9px] uppercase tracking-widest text-muted">CTR</div></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {tab === "popup" && (
                <div>
                  <div className="font-display uppercase text-2xl tracking-tight mb-1">Pop-up Ad Campaign</div>
                  <p className="font-serif-accent text-gold text-sm mb-5">Take over the deal popup for a category — 2-week run. {selBiz?.plan === "pro" && <span className="text-white">Pro subscribers: {livePrices?.pro_ad_discount_pct || 20}% off any ad-space.</span>}</p>
                  {!selBizId && <div className="border border-amber-400/30 bg-amber-400/5 p-4 rounded-sm font-serif-accent text-amber-200 mb-4">Select a business first.</div>}
                  {selBizId && (
                    <div className="mb-5">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-muted mb-2 font-display">Run this ad in which category</div>
                      <select data-testid="popup-cat-select" value={popupCat} onChange={(e) => setPopupCat(e.target.value)} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-gold/50">
                        {myBizCats.map(c => <option key={c} value={c}>{CAT_TITLE[c] || c}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                      <div className="border border-white/10 rounded-sm p-5 bg-bg/40">
                        <div className="font-display uppercase tracking-tight text-lg">Standard</div>
                        <div className="mt-2 flex items-baseline gap-2">
                          <span className="font-display text-4xl gold-text" data-testid="popup-ad-standard-price">${popupStdAmount}</span>
                          {popupStdRegular > popupStdAmount && <span className="text-muted text-base line-through opacity-60">${popupStdRegular}</span>}
                        </div>
                        <div className="text-muted text-xs uppercase tracking-widest">2-week run</div>
                        <button data-testid="popup-ad-standard" disabled={loading === "popup_ad" || !selBizId} onClick={() => buyPopupAd("popup_ad", popupCat)} className="w-full mt-5 font-display uppercase tracking-[0.2em] text-[11px] py-3 border border-white/20 hover:bg-white hover:text-bg rounded-sm disabled:opacity-50">
                          {loading === "popup_ad" ? "Redirecting…" : `Buy $${popupStdAmount}`}
                        </button>
                      </div>
                      <div className={`border rounded-sm p-5 ${selBiz?.plan === "pro" ? "border-gold/40 bg-gradient-to-br from-gold/10 to-transparent" : "border-white/5 bg-bg/20 opacity-60"}`}>
                        <div className="font-serif-accent text-gold text-xs">Pro subscribers only ~</div>
                        <div className="font-display uppercase tracking-tight text-lg">Pro {livePrices?.pro_ad_discount_pct || 20}% off</div>
                        <div className="mt-2 flex items-baseline gap-2">
                          <span className="font-display text-4xl gold-text" data-testid="popup-ad-pro-price">${popupProAmount}</span>
                          <span className="text-muted text-base line-through opacity-60">${popupStdAmount}</span>
                        </div>
                        <div className="text-muted text-xs uppercase tracking-widest">2-week run · Pro discount</div>
                        <button data-testid="popup-ad-pro" disabled={loading === "popup_ad_pro" || !selBizId || selBiz?.plan !== "pro"} onClick={() => buyPopupAd("popup_ad_pro", popupCat)} className="w-full mt-5 font-display uppercase tracking-[0.2em] text-[11px] py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-50">
                          {loading === "popup_ad_pro" ? "Redirecting…" : `Buy $${popupProAmount} (Pro)`}
                        </button>
                      </div>
                  </div>

                  {/* Pre-purchase next cycle CTA for any of my live popup ads */}
                  {selBizId && <MyPopupsPrepurchase businessId={selBizId} />}
                </div>
              )}

              {tab === "banner" && (
                <div>
                  <div className="font-display uppercase text-2xl tracking-tight mb-1">Homepage Banner</div>
                  <p className="font-serif-accent text-gold text-sm mb-5">$99 buys a 4-week takeover of one of six premium homepage banner slots. Pre-book the next window if a slot is occupied — yours auto-goes-live when the current run ends.</p>
                  {!selBizId && <div className="border border-amber-400/30 bg-amber-400/5 p-4 rounded-sm font-serif-accent text-amber-200 mb-4">Select a business first.</div>}

                  {selBizId && (
                    <>
                      <div className="mb-5">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted mb-2 font-display">Choose slot</div>
                        <div className="grid md:grid-cols-3 gap-3">
                          {Object.entries(bannerSlotsInfo.slots || {}).map(([key, meta]) => {
                            const active = bannerSlot === key;
                            const mineAll = myBanners.filter(b => b.slot === key && b.approval_status !== "rejected" && new Date(b.ends_at) > new Date());
                            const mine = mineAll.find(b => b.active && new Date(b.starts_at) <= new Date()) || mineAll[0];
                            const minePending = mine && (mine.approval_status === "pending" || !mine.active);
                            const mineQueued = mine && new Date(mine.starts_at) > new Date();
                            return (
                              <button key={key} data-testid={`banner-slot-btn-${key}`} onClick={() => setBannerSlot(key)}
                                className={`text-left border rounded-sm p-4 transition-all ${active ? "border-gold bg-gold/10" : "border-white/10 hover:border-gold/40"}`}>
                                <div className="flex items-center justify-between">
                                  <div className="font-display uppercase text-sm tracking-tight">{meta.label}</div>
                                  {meta.tag && <div className="font-serif-accent text-gold text-[11px]">{meta.tag}</div>}
                                </div>
                                <div className="text-muted text-xs font-serif-accent mt-1">{meta.blurb}</div>
                                {mine && (
                                  <div className={`text-[10px] uppercase tracking-widest font-display mt-2 ${minePending ? "text-amber-300" : "text-gold"}`}>
                                    ● yours · {minePending && mineQueued ? `pending — starts ${new Date(mine.starts_at).toLocaleDateString()}` : minePending ? "pending approval" : mineQueued ? `queued — starts ${new Date(mine.starts_at).toLocaleDateString()}` : `ends ${new Date(mine.ends_at).toLocaleDateString()}`}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {slotAvailability && (
                        <div data-testid="slot-availability" className="border border-white/10 rounded-sm p-4 mb-4 bg-bg/40">
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Status</div>
                              <div className="font-display text-sm mt-1">{slotAvailability.current ? "Running" : "Open"}</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Queue</div>
                              <div className="font-display text-sm mt-1">{slotAvailability.queue_length} queued</div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-display">Next available</div>
                              <div className="font-display text-sm mt-1">{new Date(slotAvailability.next_available_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                        </div>
                      )}

                      {(() => {
                        const nextAvail = slotAvailability?.next_available_at;
                        const willStart = nextAvail && new Date(nextAvail) > new Date() ? new Date(nextAvail) : new Date();
                        const willEnd = new Date(willStart.getTime() + 28 * 86400000);
                        return (
                          <div className="border border-gold/30 rounded-sm p-6 bg-gradient-to-br from-gold/10 to-transparent">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="font-display uppercase tracking-tight text-lg">
                                  {slotAvailability?.current ? "Pre-book this slot" : `Buy ${bannerSlotsInfo.slots?.[bannerSlot]?.label}`}
                                </div>
                                <div className="font-serif-accent text-gold text-xs mt-1">
                                  Your run: {willStart.toLocaleDateString()} → {willEnd.toLocaleDateString()}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-display text-4xl gold-text" data-testid="banner-buy-price">${bannerAmount}</div>
                                <div className="text-muted text-xs uppercase tracking-widest">{bannerSlotsInfo.duration_days || 28} days</div>
                              </div>
                            </div>
                            <button data-testid="banner-buy-btn" disabled={loading === "banner_ad"} onClick={buyBannerAd}
                              className="w-full mt-5 font-display uppercase tracking-[0.2em] text-xs py-3.5 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-60">
                              {loading === "banner_ad" ? "Redirecting…" : slotAvailability?.current ? `Pre-book — Checkout $${bannerAmount}` : `Checkout $${bannerAmount}`}
                            </button>
                          </div>
                        );
                      })()}

                      {myBanners.some(b => b.slot === bannerSlot && b.active && new Date(b.ends_at) > new Date()) && (
                        <div className="mt-5 border border-white/10 rounded-sm p-5 bg-bg/30">
                          <div className="flex items-center justify-between mb-4">
                            <div className="font-display uppercase tracking-tight text-lg">Design & Content</div>
                            <a href="https://www.canva.com/create/banners/" target="_blank" rel="noreferrer"
                              data-testid="canva-link"
                              className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-1.5 border border-white/20 hover:border-gold/40 hover:text-gold rounded-sm">
                              🎨 Design in Canva ↗
                            </a>
                          </div>
                          <div className="text-muted text-xs font-serif-accent mb-3">Create a 1920×600 banner in Canva · download · upload directly below.</div>
                          <div className="grid md:grid-cols-2 gap-3">
                            <input data-testid="banner-headline" value={bHead} onChange={(e) => setBHead(e.target.value)} placeholder="Headline" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                            <textarea data-testid="banner-body" value={bBody} onChange={(e) => setBBody(e.target.value)} rows={2} placeholder="Body (1-2 sentences)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                            <div className="md:col-span-2"><ImageUploader value={bImg} onChange={setBImg} /></div>
                            <input data-testid="banner-cta-text" value={bCtaText} onChange={(e) => setBCtaText(e.target.value)} placeholder="CTA text" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                            <input data-testid="banner-cta-url" value={bCtaUrl} onChange={(e) => setBCtaUrl(e.target.value)} placeholder="CTA URL" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                            <button data-testid="banner-save" onClick={saveBannerContent} className="md:col-span-2 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Save Banner Content</button>
                          </div>
                          {bMsg && <div className="text-sm text-gold mt-3 font-serif-accent">{bMsg}</div>}
                        </div>
                      )}

                      {/* Pre-purchase next cycle CTA (one-time only) */}
                      {(() => {
                        const myAd = myBanners.find(b => b.slot === bannerSlot && b.active && new Date(b.ends_at) > new Date());
                        return myAd ? <PrepurchaseCard adType="banner" adId={myAd.id} label={`Banner ${bannerSlot}`} testIdPrefix="banner-prepurchase" /> : null;
                      })()}
                    </>
                  )}
                </div>
              )}

              {tab === "carousel" && (
                <div data-testid="portal-carousel-tab">
                  <div className="font-display uppercase text-2xl tracking-tight mb-1">Hero Carousel Slot</div>
                  <p className="font-serif-accent text-gold text-sm mb-5">
                    ${carouselInfo.price || 49} buys a {carouselInfo.duration_days || 14}-day takeover of one of 6 hero carousel slots — auto-flips every 8s under the hero image.
                  </p>
                  {!selBizId && <div className="border border-amber-400/30 bg-amber-400/5 p-4 rounded-sm font-serif-accent text-amber-200 mb-4">Select a business first.</div>}
                  {selBizId && (
                    <>
                      <div className="mb-5">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-muted mb-2 font-display">Choose slot</div>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                          {(carouselSlots.length > 0 ? carouselSlots : (carouselInfo.slots || []).map(s => ({ slot: s, status: "open" }))).map((s) => {
                            const active = carouselSlot === s.slot;
                            const mineAll = myCarousels.filter(c => c.slot === s.slot && c.approval_status !== "rejected" && new Date(c.ends_at) > new Date());
                            const mine = mineAll.find(c => c.active && new Date(c.starts_at) <= new Date()) || mineAll[0];
                            const minePending = mine && (mine.approval_status === "pending" || !mine.active);
                            const mineQueued = mine && new Date(mine.starts_at) > new Date();
                            return (
                              <button key={s.slot} data-testid={`portal-carousel-slot-${s.slot}`} onClick={() => setCarouselSlot(s.slot)}
                                className={`text-left border rounded-sm p-4 transition-all ${active ? "border-gold bg-gold/10" : s.status === "paid" ? "border-rose-500/40 bg-rose-500/5 opacity-70" : "border-white/10 hover:border-gold/40"}`}>
                                <div className="flex items-center justify-between">
                                  <div className="font-display uppercase text-sm tracking-tight">{s.slot.replace("_", " ")}</div>
                                  <div className={`text-[9px] uppercase tracking-widest font-display px-2 py-0.5 rounded-sm ${s.status === "paid" ? "bg-rose-500/20 text-rose-300" : s.status === "seed" ? "bg-white/10 text-muted" : "bg-gold/15 text-gold"}`}>{s.status === "open" ? "Open" : s.status === "paid" ? "Taken" : "Featured"}</div>
                                </div>
                                <div className="text-muted text-xs font-serif-accent mt-1 truncate">{s.headline || "Available — first to claim"}</div>
                                {mine && (
                                  <div className={`text-[10px] uppercase tracking-widest font-display mt-2 ${minePending ? "text-amber-300" : "text-gold"}`}>
                                    ● yours · {minePending && mineQueued ? `pending — starts ${new Date(mine.starts_at).toLocaleDateString()}` : minePending ? "pending approval" : mineQueued ? `queued — starts ${new Date(mine.starts_at).toLocaleDateString()}` : `ends ${new Date(mine.ends_at).toLocaleDateString()}`}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="border border-gold/30 rounded-sm p-6 bg-gradient-to-br from-gold/10 to-transparent">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-display uppercase tracking-tight text-lg">Buy this slot</div>
                            <div className="font-serif-accent text-gold text-xs mt-1">{carouselInfo.duration_days || 14}-day run · auto-flips with the wheel · clicks open your business detail</div>
                          </div>
                          <div className="text-right">
                            <div className="font-display text-4xl gold-text" data-testid="carousel-buy-price">${carouselAmount}</div>
                            <div className="text-muted text-xs uppercase tracking-widest">{carouselInfo.duration_days || 14} days</div>
                          </div>
                        </div>
                        <button data-testid="carousel-buy-btn" disabled={loading === "carousel_ad"} onClick={buyCarouselAd}
                          className="w-full mt-5 font-display uppercase tracking-[0.2em] text-xs py-3.5 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-60">
                          {loading === "carousel_ad" ? "Redirecting…" : `Checkout $${carouselAmount}`}
                        </button>
                      </div>

                      {myCarousels.some(c => c.slot === carouselSlot && c.active && new Date(c.ends_at) > new Date()) && (
                        <div className="mt-5 border border-white/10 rounded-sm p-5 bg-bg/30">
                          <div className="font-display uppercase tracking-tight text-lg mb-3">Design & Content</div>
                          <div className="text-muted text-xs font-serif-accent mb-3">Recommended image: 1680×640 (21:8). Drag-to-pan in the cropper. <span className="text-gold">↓ Preview shows exactly how your ad will look on the homepage.</span></div>

                          {/* LIVE PREVIEW — matches CarouselWheel rendering exactly */}
                          <div className="mb-4">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-display mb-2">↓ Live homepage preview</div>
                            <div className="relative rounded-sm overflow-hidden border border-gold/30 bg-bg/40" style={{ aspectRatio: "16/9" }} data-testid="carousel-preview">
                              {carImg ? (
                                <>
                                  <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${carImg}')` }} />
                                  <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/60 to-transparent" />
                                  <div className="absolute inset-x-0 bottom-0 px-6 sm:px-10 py-5 sm:py-6 flex items-end justify-between gap-4">
                                    <div className="max-w-[70%] sm:max-w-2xl min-w-0">
                                      <div className="font-serif-accent text-gold text-[10px] sm:text-xs uppercase tracking-[0.3em]">Sponsored</div>
                                      <div className="font-display uppercase text-lg sm:text-3xl tracking-tight mt-1 line-clamp-2">{carHead || "Your headline appears here"}</div>
                                      {carBody && <div className="text-white/80 text-xs sm:text-sm mt-1 sm:mt-2 line-clamp-2">{carBody}</div>}
                                    </div>
                                    <div className="shrink-0 inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-gold-grad text-bg rounded-sm font-display uppercase text-[10px] sm:text-xs tracking-[0.2em] shadow-lg shadow-gold/20">
                                      {carCtaText || "Visit"} →
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-muted text-sm font-serif-accent italic">Upload an image to see your live preview ↓</div>
                              )}
                            </div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-3">
                            <input data-testid="carousel-headline" value={carHead} onChange={(e) => setCarHead(e.target.value)} placeholder="Headline" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                            <textarea data-testid="carousel-body" value={carBody} onChange={(e) => setCarBody(e.target.value)} rows={2} placeholder="Body / tagline (optional)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm md:col-span-2" />
                            <div className="md:col-span-2"><ImageUploader value={carImg} onChange={setCarImg} aspectRatio="16/9" /></div>
                            <input data-testid="carousel-cta-text" value={carCtaText} onChange={(e) => setCarCtaText(e.target.value)} placeholder="CTA text" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                            <input data-testid="carousel-cta-url" value={carCtaUrl} onChange={(e) => setCarCtaUrl(e.target.value)} placeholder="CTA URL (optional)" className="bg-bg border border-white/10 p-3 rounded-sm text-sm" />
                            <button data-testid="carousel-save-content" onClick={saveCarouselContent} className="md:col-span-2 font-display uppercase tracking-[0.2em] text-xs py-3 bg-gold-grad text-bg rounded-sm hover:brightness-110">Save Carousel Content</button>
                          </div>
                          {carMsg && <div className="text-sm text-gold mt-3 font-serif-accent">{carMsg}</div>}
                        </div>
                      )}

                      {/* Pre-purchase next cycle CTA (one-time only) */}
                      {(() => {
                        const myAd = myCarousels.find(c => c.slot === carouselSlot && c.active && new Date(c.ends_at) > new Date());
                        return myAd ? <PrepurchaseCard adType="carousel" adId={myAd.id} label={`Carousel ${carouselSlot}`} testIdPrefix="carousel-prepurchase" /> : null;
                      })()}
                    </>
                  )}
                </div>
              )}

              {tab === "emails" && (
                <div className="p-6 md:p-8 space-y-4" data-testid="portal-emails">
                  <div>
                    <div className="font-serif-accent text-gold text-sm">~ Subscribers to {selBiz?.name || "your listing"}</div>
                    <div className="font-display uppercase text-2xl tracking-tight">Your email list</div>
                    <div className="text-muted text-sm mt-1 font-serif-accent">Everyone who opted in via the "Get updates" form on your business page. Yours to keep — export anytime.</div>
                  </div>
                  <BizOwnerEmailList businessId={selBizId} />
                </div>
              )}

              {tab === "casino" && (
                <div data-testid="portal-casino">
                  <CasinoOwnerTab businesses={businesses} onOpenPortalBiz={(bizId) => { setSelBizId(bizId); setTab("listings"); }} />
                </div>
              )}

            </div>

            {showLeads && (
              <div data-testid="leads-modal" className="absolute inset-0 z-10 bg-surface/98 backdrop-blur p-6 md:p-8 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-serif-accent text-gold text-sm">~ Your captured leads</div>
                    <div className="font-display uppercase text-2xl tracking-tight">{leads.length} {leads.length === 1 ? "lead" : "leads"}</div>
                  </div>
                  <div className="flex gap-2">
                    {leads.length > 0 && <button data-testid="leads-csv" onClick={downloadLeadsCsv} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-gold/40 text-gold hover:bg-gold hover:text-bg rounded-sm">⬇ CSV</button>}
                    <button data-testid="leads-close" onClick={() => setShowLeads(false)} className="font-display uppercase text-[10px] tracking-[0.2em] px-3 py-2 border border-white/20 hover:border-white/40 rounded-sm">← Back</button>
                  </div>
                </div>
                {leads.length === 0 && <div className="text-muted text-center py-12 font-serif-accent">No leads yet. Once visitors tap "Continue with Google" on your popup, they'll show up here.</div>}
                {leads.length > 0 && (
                  <div className="border border-white/10 rounded-sm overflow-hidden">
                    <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 bg-bg/50 text-[10px] uppercase tracking-[0.22em] text-muted font-display">
                      <div className="col-span-4">Email</div>
                      <div className="col-span-3">Name</div>
                      <div className="col-span-2">Category</div>
                      <div className="col-span-2">Code</div>
                      <div className="col-span-1 text-right">Date</div>
                    </div>
                    {leads.map(l => (
                      <div key={l.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 last:border-0 items-center text-sm">
                        <div className="col-span-4 truncate">{l.email}</div>
                        <div className="col-span-3 truncate text-muted">{l.name}</div>
                        <div className="col-span-2 text-muted text-xs font-serif-accent">{CAT_TITLE[l.category] || l.category}</div>
                        <div className="col-span-2 font-display gold-text tracking-tight">{l.redemption_code}</div>
                        <div className="col-span-1 text-right text-muted text-xs font-mono">{new Date(l.captured_at).toLocaleDateString()}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
      {extraCatOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl"
          onClick={() => !extraCatBusy && setExtraCatOpen(false)}
          data-testid="extra-category-modal">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }} transition={{ type: "spring", damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-surface border-2 border-pink-500/40 rounded-sm p-7 ad-247-glow">
            <div className="inline-block font-display uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 rounded-sm bg-pink-500 text-white">Add-on</div>
            <div className="font-display uppercase text-3xl tracking-tight mt-3">Purchase Additional Category</div>
            <div className="text-muted text-sm mt-2 font-serif-accent">List <span className="text-white">{selBiz?.name || "your business"}</span> in one more category for just <span className="text-gold font-display">$9.99/month</span>. Recurs monthly · cancel anytime from Manage Billing.</div>

            <div className="mt-5">
              <label className="block text-[10px] uppercase tracking-[0.22em] text-muted font-display mb-2">Pick a category</label>
              <select data-testid="extra-cat-select" value={extraCatChoice} onChange={(e) => setExtraCatChoice(e.target.value)} className="w-full bg-bg border border-white/10 p-3 rounded-sm text-sm focus:border-pink-500/60">
                <option value="">— Choose category —</option>
                {CATEGORIES.filter(c => !c.isHappening && !(selBiz?.categories || [selBiz?.category]).includes(c.key)).map(c => (
                  <option key={c.key} value={c.key}>{c.title}</option>
                ))}
              </select>
            </div>

            {extraCatErr && <div className="mt-3 p-3 border border-red-500/40 bg-red-500/10 rounded-sm text-red-300 text-sm">{extraCatErr}</div>}

            <div className="mt-5 flex gap-2">
              <button data-testid="extra-cat-buy" disabled={extraCatBusy || !extraCatChoice} onClick={buyExtraCategory}
                className="flex-1 font-display uppercase tracking-[0.2em] text-sm py-3 bg-pink-500 text-white rounded-sm hover:brightness-110 disabled:opacity-50 shadow-lg shadow-pink-500/40">
                {extraCatBusy ? "Redirecting…" : "Continue to Checkout — $9.99/mo"}
              </button>
              <button data-testid="extra-cat-cancel" disabled={extraCatBusy} onClick={() => setExtraCatOpen(false)} className="font-display uppercase tracking-[0.2em] text-[11px] py-3 px-4 border border-white/15 rounded-sm hover:border-white/30">
                Later
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

    </AnimatePresence>
  );
}

function PaymentStatus({ sessionId, onClose }) {
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      if (attempts.current >= 8) return setStatus("timeout");
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid" || res.data.status === "complete") setStatus("paid");
        else if (res.data.status === "expired") setStatus("expired");
        else setTimeout(poll, 2200);
      } catch { setTimeout(poll, 2200); }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/95 backdrop-blur-2xl">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-surface border border-gold/30 p-10 rounded-sm text-center max-w-md">
        {status === "checking" && (<><div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-5" /><div className="font-display text-xl uppercase tracking-tight">Confirming Payment…</div><div className="text-muted text-sm mt-2 font-serif-accent">Holding the line with Stripe ~</div></>)}
        {status === "paid" && (<><div className="text-5xl gold-text font-display mb-3">✓</div><div className="font-display text-2xl uppercase tracking-tight gold-text">Payment Successful</div><div className="text-muted text-sm mt-2">Ad goes live once admin approves it. If the slot's busy, yours queues up — you'll see your start date in the Banner / Carousel tab. <span className="text-gold">Open your Business Portal to reserve the next cycle at 10% off (one-time offer).</span></div><button data-testid="payment-close-btn" onClick={onClose} className="mt-6 px-6 py-3 border border-gold/40 hover:bg-gold hover:text-bg rounded-sm font-display uppercase text-xs tracking-[0.22em]">Close</button></>)}
        {(status === "expired" || status === "timeout") && (<><div className="font-display text-xl uppercase tracking-tight">Payment {status}</div><div className="text-muted text-sm mt-2">Please try again or contact support.</div><button data-testid="payment-close-btn" onClick={onClose} className="mt-6 px-6 py-3 border border-white/20 rounded-sm font-display uppercase text-xs tracking-[0.22em]">Close</button></>)}
      </motion.div>
    </div>
  );
}

function NewsletterPopup({ onSubscribe }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem("zc_newsletter_seen");
    if (seen) return;

    // Only show the newsletter popup AFTER the user has engaged with the page.
    // Previously we auto-opened after 1.8s — which on slow mobile felt like the
    // page was "stuck in opening mode" because the popup slammed shut over the
    // homepage before users even saw the categories. Now we trigger on:
    //   • Scroll past 25% of the page (proves user is browsing), OR
    //   • A safety timer of 25s if they're idle, OR
    //   • Exit-intent on desktop (mouse leaves the top of the viewport).
    let triggered = false;
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setOpen(true);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onExit);
      clearTimeout(safety);
    };
    const onScroll = () => {
      const pct = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (pct > 0.25) trigger();
    };
    const onExit = (e) => {
      // Desktop exit-intent only — clientY <= 0 means mouse left top of viewport
      if (e.clientY <= 0) trigger();
    };
    const safety = setTimeout(trigger, 25000);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseleave", onExit);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onExit);
      clearTimeout(safety);
    };
  }, []);

  const close = () => {
    localStorage.setItem("zc_newsletter_seen", "1");
    setOpen(false);
  };

  const submit = async () => {
    if (!email.includes("@")) return setMsg("Enter a valid email");
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setMsg("✓ You're in. We'll only ring you for the best stuff.");
      onSubscribe?.();
      setTimeout(close, 1500);
    } catch { setMsg("Error — try again"); }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[105] flex items-center justify-center p-4 bg-black/85 backdrop-blur-xl" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={close}>
          <motion.div data-testid="newsletter-popup" onClick={(e) => e.stopPropagation()} initial={{ y: 30, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0 }} className="relative w-full max-w-md bg-surface border border-gold/40 rounded-sm overflow-hidden marquee-glow">
            <div className="h-44 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=600')" }}>
              <div className="absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-transparent to-surface" />
            </div>
            <button data-testid="newsletter-popup-close" onClick={close} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 hover:bg-bg text-white flex items-center justify-center z-10">✕</button>
            <div className="p-6 lg:p-8 -mt-12 relative">
              <div className="font-serif-accent text-gold text-base">~ The Insider's Hotline</div>
              <div className="font-display uppercase text-3xl tracking-tight mt-1">Vegas Deals — Free</div>
              <div className="text-muted text-sm mt-2">Weekly drop of dayclub comps, residency presales, and locals-only nights. Your inbox, your rules.</div>
              <div className="flex flex-col sm:flex-row gap-2 mt-5">
                <input data-testid="newsletter-popup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@vegas.com" className="flex-1 bg-bg border border-white/10 p-3.5 rounded-sm text-sm focus:border-gold/40" />
                <button data-testid="newsletter-popup-submit" disabled={busy} onClick={submit} className="font-display uppercase tracking-[0.2em] text-xs py-3.5 px-6 bg-gold-grad text-bg rounded-sm hover:brightness-110 disabled:opacity-60">{busy ? "…" : "Subscribe"}</button>
              </div>
              {msg && <div data-testid="newsletter-popup-msg" className="text-gold mt-3 font-serif-accent text-sm">{msg}</div>}
              <button onClick={close} className="text-xs text-muted/60 mt-4 underline-offset-2 hover:underline">No thanks, I hate good deals.</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async () => {
    if (!email.includes("@")) return setMsg("Enter a valid email");
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      setMsg("✓ You're in. We'll only ring you for the best stuff.");
      setEmail("");
    } catch (e) { setMsg("Error — try again"); }
  };
  return (
    <div className="max-w-xl mx-auto">
      <div className="font-serif-accent text-gold text-lg mb-2">~ Deals hotline</div>
      <div className="font-display uppercase text-2xl tracking-tight mb-3">Get new Vegas deals in your inbox</div>
      <p className="text-muted text-sm mb-5">Weekly drops of residency discounts, dayclub comps, and hidden locals-only nights. No spam. Unsubscribe any time.</p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input data-testid="newsletter-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@vegas.com" className="flex-1 bg-surface border border-white/10 p-3.5 rounded-sm text-sm focus:border-gold/40 focus:bg-bg" />
        <button data-testid="newsletter-submit" onClick={submit} className="font-display uppercase tracking-[0.2em] text-xs py-3.5 px-6 bg-gold-grad text-bg rounded-sm hover:brightness-110">Subscribe</button>
      </div>
      {msg && <div className="text-gold mt-3 font-serif-accent text-sm" data-testid="newsletter-msg">{msg}</div>}
    </div>
  );
}

function RefPromoBanner() {
  const [ref, setRef] = useState(null);
  const [valid, setValid] = useState(false);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const candidate = p.get("ref") || localStorage.getItem("zc_ref");
    if (!candidate) return;
    axios.get(`${API}/affiliate/validate/${candidate}`).then(r => {
      if (r.data?.valid) {
        localStorage.setItem("zc_ref", candidate);
        setRef(candidate);
        setValid(true);
      } else {
        localStorage.removeItem("zc_ref");
      }
    }).catch(() => {});
  }, []);
  if (!ref || !valid) return null;
  // Silently persist the code for attribution but hide the banner — businesses
  // shouldn't see "referred by XYZ" messaging when they arrive via QR scan.
  return null;
  // Legacy promo banner (disabled):
  // return (
  //   <div data-testid="ref-promo-banner" className="bg-gold text-bg px-4 py-2.5 text-center font-display uppercase text-[11px] tracking-[0.2em]">
  //     Referred by <span className="font-serif-accent lowercase normal-case tracking-normal"> · code {ref} · </span> first month free on any plan
  //   </div>
  // );
}

function HomePage() {
  useCategoriesLive(); // re-render when admin-managed categories update
  const { user, logout, refresh } = useAuth();
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [popup, setPopup] = useState(null);
  const [currentCat, setCurrentCat] = useState(null);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [portalOpen, setPortalOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInInitialMode, setSignInInitialMode] = useState("owner");
  const [freeEventOpen, setFreeEventOpen] = useState(false);
  // Initialize businesses from localStorage cache so the homepage paints
  // instantly (within ~50ms of mount) — without this, the user stares at
  // empty category grids while the backend takes 5-60s to wake up from a
  // cold start. The API refresh below updates state in the background.
  const [businesses, setBusinesses] = useState(() => {
    try {
      const cached = localStorage.getItem("zc_businesses_cache_v1");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) return parsed;
      }
    } catch { /* noop */ }
    return [];
  });
  const [paymentSession, setPaymentSession] = useState(null);
  const [banners, setBanners] = useState({});
  // Brand social links pulled from /api/plans.brand_socials — admin-editable in Settings.
  const [brandSocials, setBrandSocials] = useState(null);
  useEffect(() => {
    axios.get(`${API}/plans`).then(r => { setBrandSocials(r.data?.brand_socials || null); }).catch(() => {});
  }, []);
  const [pendingAdultCat, setPendingAdultCat] = useState(null);
  const [adultVerified, setAdultVerified] = useState(() => sessionStorage.getItem("zc_adult_ok") === "1");

  const [pendingAfterAuth, setPendingAfterAuth] = useState(null); // "portal" | null
  const requireAuth = (cb, intent = null) => {
    if (user) return cb();
    setPendingAfterAuth(intent);
    setSignInOpen(true);
  };
  const openPortal = () => requireAuth(() => setPortalOpen(true), "portal");

  // When sign-in completes, pick up where the user left off
  useEffect(() => {
    if (user && pendingAfterAuth === "portal") {
      setPortalOpen(true);
      setPendingAfterAuth(null);
    }
  }, [user, pendingAfterAuth]);

  // Track site visit (once per session) for admin analytics
  useEffect(() => {
    if (sessionStorage.getItem("zc_site_tracked") === "1") return;
    axios.post(`${API}/analytics/track`, { event: "site_visit" }).catch(() => {});
    sessionStorage.setItem("zc_site_tracked", "1");
  }, []);

  const requestCategoryOpen = (catKey) => {
    const cat = CATEGORIES.find(c => c.key === catKey);
    if (!cat) return;
    if (ADULT_CATEGORIES.has(catKey) && !adultVerified) {
      setPendingAdultCat(cat);
      return;
    }
    // Track category open for admin analytics
    axios.post(`${API}/analytics/track`, { event: "category_visit", key: catKey }).catch(() => {});
    setPopup(cat);
    // Push to browser history so the back button closes the popup
    // instead of leaving the site entirely.
    window.history.pushState({ zcModal: "popup", cat: catKey }, "", `#cat/${catKey}`);
  };

  // Browser back-arrow: close ONE overlay at a time (top-down) instead of nuking everything.
  // This is the natural-feel behavior the user expected — back-arrow on a biz detail closes
  // it and returns to the category subcategory view; another back closes the category view;
  // another back returns to homepage scroll position.
  useEffect(() => {
    const onPop = () => {
      // Decide which single overlay to close based on what's currently open.
      // Priority order: BusinessDetail (most-recent) → DealPopup → SubcategoryView → Portal → FreeEventModal
      if (selectedBiz) { setSelectedBiz(null); return; }
      if (popup)       { setPopup(null);       return; }
      if (currentCat)  { setCurrentCat(null);  return; }
      if (portalOpen)  { setPortalOpen(false); return; }
      if (freeEventOpen) { setFreeEventOpen(false); return; }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [selectedBiz, popup, currentCat, portalOpen, freeEventOpen]);

  const confirmAdult = () => {
    sessionStorage.setItem("zc_adult_ok", "1");
    setAdultVerified(true);
    if (pendingAdultCat) setPopup(pendingAdultCat);
    setPendingAdultCat(null);
  };
  const denyAdult = () => setPendingAdultCat(null);

  const reloadBusinesses = () => axios.get(`${API}/businesses?slim=true`).then((r) => {
    setBusinesses(r.data);
    // Persist for next cold start — stale-while-revalidate pattern: returning
    // visitors see content instantly, the API refreshes silently.
    try { localStorage.setItem("zc_businesses_cache_v1", JSON.stringify(r.data)); } catch { /* quota */ }
  }).catch(() => {});
  const reloadBanners = () => axios.get(`${API}/banner-ads/active`).then(r => setBanners(r.data || {})).catch(() => {});

  useEffect(() => {
    reloadBusinesses();
    // Defer non-critical banner fetch so the homepage paints fast.
    setTimeout(reloadBanners, 0);
    const p = new URLSearchParams(window.location.search);
    if (p.get("payment") === "success" && p.get("session_id")) setPaymentSession(p.get("session_id"));
    // Shared-link arrival: open the business modal directly.
    const sharedBizId = p.get("biz");
    if (sharedBizId) {
      axios.get(`${API}/businesses/${sharedBizId}`).then(r => setSelectedBiz(r.data)).catch(() => {});
      // Clean up the query param so the user can navigate normally afterward.
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Did the user just subscribe? Auto-open the Business Portal and surface
    // the "Purchase Additional Category" upsell (handled inside BusinessPortal).
    if (p.get("payment") === "success" && !p.get("extra_category")) {
      sessionStorage.setItem("zc_show_extra_cat_upsell", "1");
      setPortalOpen(true);
    }
    // QR-scan flow: when a business owner arrives via a ref link, auto-launch the
    // Business Portal signup. The ref code is silently persisted in localStorage
    // by RefPromoBanner's validation effect — but we HIDE the yellow banner from
    // the business so it feels like a normal "claim your business" entry.
    if (p.get("ref")) {
      setPendingAfterAuth("portal");
      setSignInOpen(true);
      // Clean up the ?ref= from the visible URL (but keep it in localStorage for attribution)
      window.history.replaceState({}, "", window.location.pathname);
    }
    // Team-member invite link — auto-open sign-in in team mode
    const tmEmail = p.get("team_login");
    if (tmEmail) {
      try { localStorage.setItem("zc_last_email", tmEmail); } catch {}
      setSignInInitialMode("team");
      setSignInOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const closePayment = () => { setPaymentSession(null); window.history.replaceState({}, "", "/"); reloadBusinesses(); reloadBanners(); };

  const { lang } = useI18n();
  const filteredCats = CATEGORIES.filter((c) => {
    const q = query.toLowerCase();
    const matchSearch = !q || (c.title || "").toLowerCase().includes(q) || tCatTitle(c, lang).toLowerCase().includes(q);
    const matchFilter = activeFilter === "all" || c.key === activeFilter;
    return matchSearch && matchFilter;
  });

  // Direct business hits — only when user is searching (no category filter active)
  const trimmed = query.trim().toLowerCase();
  const matchedBizs = (trimmed.length >= 2 && activeFilter === "all")
    ? businesses.filter((b) => {
        const hay = `${b.name || ""} ${b.subcategory || ""} ${b.address || ""} ${b.desc || ""}`.toLowerCase();
        return hay.includes(trimmed);
      }).slice(0, 24)
    : [];

  const catItems = currentCat
    ? businesses.filter((b) => b.category === currentCat.key || (b.extra_categories || []).includes(currentCat.key))
    : [];

  // Banner slot positions on homepage (interspersed)
  // Distribute 8 banner slots evenly across ~40 categories — one per ~5 rows.
  const slotByIndex = { 2: "new_deals", 7: "after_hours", 12: "weekender", 17: "locals_only", 22: "vip_only", 27: "hidden_gems", 32: "happy_hour", 37: "poolside" };

  return (
    <div className="min-h-screen bg-bg text-white grain">
      <RefPromoBanner />
      <TopNav
        onBusinessClick={openPortal}
        onCasinoTeamClick={() => {
          if (user) {
            // Owner already signed in — jump straight to Business Portal so they see the Casino tab.
            openPortal();
          } else {
            setSignInInitialMode("team");
            setSignInOpen(true);
          }
        }}
        onHome={() => { setCurrentCat(null); setSelectedBiz(null); }} query={query} setQuery={setQuery} activeFilter={activeFilter} setActiveFilter={setActiveFilter} user={user} onLogin={() => setSignInOpen(true)} onLogout={logout} />

      {!currentCat && (
        <>
          <Hero />
          <CarouselWheel businesses={businesses} onOpenBusiness={setSelectedBiz} />
          <section>
            {filteredCats.map((cat, i) => {
              const isFullList = activeFilter === "all" && !query;
              const slotKey = isFullList ? slotByIndex[i] : null;
              const bannerAfter = slotKey ? (
                <BannerAdSlot
                  key={`banner-${slotKey}`}
                  slot={slotKey}
                  slotData={banners[slotKey]}
                  businesses={businesses}
                  onOpenBusiness={setSelectedBiz}
                />
              ) : null;
              return (
                <React.Fragment key={cat.key}>
                  <CategoryBar cat={cat} index={i} onClick={requestCategoryOpen} />
                  {bannerAfter}
                </React.Fragment>
              );
            })}
            {!filteredCats.length && !matchedBizs.length && <div className="text-center py-24 font-serif-accent text-muted text-xl">No results match your search.</div>}
          </section>

          {matchedBizs.length > 0 && (
            <section data-testid="search-business-results" className="border-t border-white/5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <div className="font-serif-accent text-gold text-sm mb-2">~ Businesses matching "{query}"</div>
              <div className="font-display uppercase text-xl sm:text-2xl tracking-tight mb-6">{matchedBizs.length} {matchedBizs.length === 1 ? "result" : "results"}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {matchedBizs.map(b => {
                  const cat = CATEGORIES.find(c => c.key === b.category);
                  return (
                    <button
                      key={b.id}
                      data-testid={`search-result-${b.id}`}
                      onClick={() => setSelectedBiz(b)}
                      className="text-left border border-white/10 hover:border-gold/60 rounded-sm overflow-hidden bg-bg/40 transition-colors group"
                    >
                      <div className="aspect-[4/3] bg-cover bg-center" style={{ backgroundImage: `url('${b.img || ""}')` }} />
                      <div className="p-3">
                        <div className="font-display uppercase text-sm tracking-tight truncate group-hover:text-gold transition-colors">{b.name}</div>
                        <div className="text-muted text-[10px] uppercase tracking-widest font-display mt-1 truncate">
                          {cat?.title || b.category}{b.subcategory ? ` · ${b.subcategory}` : ""}
                        </div>
                        {b.offer && <div className="text-gold text-xs mt-1.5 truncate">{b.offer}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}
          <footer className="border-t border-white/5 mt-20 py-16 px-4 text-center text-muted text-sm">
            {/* Public free-event submission banner */}
            <div data-testid="free-event-banner" className="max-w-3xl mx-auto mb-12 px-6 py-7 border border-gold/30 rounded-sm bg-gradient-to-br from-gold/10 via-fuchsia-500/5 to-transparent">
              <div className="font-serif-accent text-gold text-sm">~ Hosting something this week?</div>
              <div className="font-display uppercase text-xl sm:text-2xl tracking-tight mt-1">Post your event for free</div>
              <div className="text-muted text-xs mt-2 max-w-md mx-auto">No account, no fee — submit your event and we'll feature it in <span className="text-white">Happening This Week</span> after a quick review.</div>
              <button data-testid="open-free-event-btn" onClick={() => setFreeEventOpen(true)} className="mt-4 font-display uppercase tracking-[0.2em] text-[11px] py-2.5 px-6 bg-gold-grad text-bg rounded-sm hover:brightness-110">
                Submit your event →
              </button>
            </div>
            <NewsletterSignup />
            <div className="mt-14 border-t border-white/5 pt-8">
              <div className="font-serif-accent text-gold text-lg">Zip City</div>
              <div className="mt-1">© {new Date().getFullYear()} — A neon love letter to Las Vegas.</div>
              <div className="mt-3">
                <a data-testid="footer-email-link" href="mailto:zipcitylv@gmail.com" className="text-gold hover:underline text-sm font-serif-accent">zipcitylv@gmail.com</a>
              </div>
              <div data-testid="footer-socials" className="mt-5 flex items-center justify-center gap-3">
                <a data-testid="footer-social-x" href={brandSocials?.x || "https://x.com/zipcitylv?s=11&t=OhGDVypDJkq0rP5CFaJ8sQ"} target="_blank" rel="noreferrer noopener" aria-label="Follow ZipCityLV on X" title="X (Twitter)" className="w-11 h-11 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-neutral-900 via-black to-neutral-800 border border-white/20 hover:scale-105 hover:shadow-lg transition-transform">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a data-testid="footer-social-instagram" href={brandSocials?.instagram || "https://www.instagram.com/zipcity.lv?igsh=dGluMWt6ODQzaXFl&utm_source=qr"} target="_blank" rel="noreferrer noopener" aria-label="Follow ZipCityLV on Instagram" title="Instagram" className="w-11 h-11 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-fuchsia-500 via-pink-500 to-orange-400 hover:scale-105 hover:shadow-lg transition-transform">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.06.42 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.42 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.06.37-2.23.42-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.42-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.17-.42-.37-1.06-.42-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.42-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.06-.37 2.23-.42C8.42 2.21 8.8 2.2 12 2.2M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.38C1.35 2.68.94 3.35.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.66.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.38.66-.66 1.07-1.34 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.38-2.13C21.32 1.35 20.65.94 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 100 12.32 6.16 6.16 0 000-12.32zm0 10.16a4 4 0 110-8 4 4 0 010 8zm6.4-11.85a1.44 1.44 0 100 2.88 1.44 1.44 0 000-2.88z"/></svg>
                </a>
                <a data-testid="footer-social-tiktok" href={brandSocials?.tiktok || "https://www.tiktok.com/@zipcitylv?_r=1&_t=ZS-98JYaqED28H"} target="_blank" rel="noreferrer noopener" aria-label="Follow ZipCityLV on TikTok" title="TikTok" className="w-11 h-11 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-cyan-400 via-black to-rose-500 hover:scale-105 hover:shadow-lg transition-transform">
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.53.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-display uppercase tracking-[0.22em]">
                <Link to="/affiliate" data-testid="footer-affiliate-link" className="hover:text-gold transition-colors">Influencer · Affiliate Program</Link>
                <span className="text-muted/40">·</span>
                <button onClick={openPortal} className="hover:text-gold transition-colors">Business</button>
                <span className="text-muted/40">·</span>
                <Link to="/terms" data-testid="footer-terms-link" className="hover:text-gold transition-colors">Terms</Link>
                <span className="text-muted/40">·</span>
                <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-gold transition-colors">Privacy</Link>
              </div>
            </div>
          </footer>
        </>
      )}

      {currentCat && currentCat.isHappening && <HappeningView onBack={() => setCurrentCat(null)} onPostEvent={() => setFreeEventOpen(true)} onOpenBusiness={(bizId) => {
        axios.get(`${API}/businesses/${bizId}`).then(r => { setCurrentCat(null); setSelectedBiz(r.data); }).catch(() => {});
      }} />}
      {currentCat && !currentCat.isHappening && <SubcategoryView cat={currentCat} items={catItems} onBack={() => setCurrentCat(null)} onSelect={setSelectedBiz} />}

      <DealPopup cat={popup} onClose={() => { setPopup(null); if (window.location.hash.startsWith("#cat/")) window.history.back(); }}
        onEnter={() => { setCurrentCat(popup); setPopup(null); window.scrollTo({ top: 0, behavior: "smooth" }); window.history.pushState({ zcModal: "subcat" }, "", `#sub/${popup.key}`); }}
        onGoToBusiness={(b) => {
          // Drop the user inside the popup's category so closing the business
          // modal returns them to the category list — NOT to the main grid.
          // Previously we only set selectedBiz, so closing the modal landed the
          // user back on the homepage which felt like the app forgot where they were.
          if (popup) setCurrentCat(popup);
          setPopup(null);
          setSelectedBiz(b);
          window.history.pushState({ zcModal: "biz" }, "", `#biz/${b.id}`);
        }}
        businesses={businesses}
        user={user}
        onLogin={() => setSignInOpen(true)} />

      <BusinessDetail biz={selectedBiz} currentCategory={currentCat?.key} onClose={() => setSelectedBiz(null)} />

      <BusinessPortal open={portalOpen} onClose={() => setPortalOpen(false)} businesses={businesses} onBusinessesChange={reloadBusinesses} onRefreshAuth={refresh} />

      {paymentSession && <PaymentStatus sessionId={paymentSession} onClose={closePayment} />}

      <AgeGate open={!!pendingAdultCat} onConfirm={confirmAdult} onDeny={denyAdult} />

      <SignInModal
        open={signInOpen}
        onClose={() => { setSignInOpen(false); setSignInInitialMode("owner"); }}
        message="Business owners and affiliates — no password needed. Just name and email."
        initialMode={signInInitialMode}
      />

      <NewsletterPopup />
      <FreeEventModal open={freeEventOpen} onClose={() => setFreeEventOpen(false)} />
      <TeamPasswordResetModal />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check: handle OAuth callback BEFORE normal routes render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/affiliate" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gold font-serif-accent">Loading…</div>}><AffiliatePage /></Suspense>} />
      <Route path="/admin" element={<Suspense fallback={<div className="min-h-screen flex items-center justify-center text-gold font-serif-accent">Loading admin…</div>}><AdminPage /></Suspense>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
    </Routes>
  );
}

export default function App() {
  // Bootstrap dynamic categories (admin-managed images / order / additions).
  // Retry up to 3x with backoff so a single transient 502 from Cloudflare/backend
  // doesn't leave the homepage with stale CATEGORY_DEFAULTS forever.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        try {
          const r = await axios.get(`${API}/categories/full?_=${Date.now()}`);
          if (cancelled) return;
          __setLiveCategories(r.data || []);
          return;
        } catch {
          if (attempt < 2) await new Promise(res => setTimeout(res, 800 * (attempt + 1)));
        }
      }
    };
    load();
    // Keep-alive ping every 4 minutes — prevents the production serverless
    // container from scaling to zero during quiet periods. Without this, the
    // first user after a 10-minute idle window pays a 5-15s cold-start. With
    // this, the container stays warm as long as ANY tab is open.
    const keepAlive = setInterval(() => {
      fetch(`${API}/health`, { cache: "no-store", keepalive: true }).catch(() => {});
    }, 4 * 60 * 1000);
    // Warm-up: if the user has an existing admin token, eagerly fetch the admin
    // chunk so the /admin click is instant. Without this, the user hits the
    // Suspense fallback on every cold visit (= "stuck loading" complaint).
    try {
      if (localStorage.getItem("zc_admin_token")) {
        import(/* webpackChunkName: "admin", webpackPrefetch: true */ "./AdminPage");
      }
    } catch (_) { /* noop */ }
    return () => { cancelled = true; clearInterval(keepAlive); };
  }, []);

  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
